​import { Component, ViewChild, ElementRef, OnInit, OnDestroy, ChangeDetectorRef, NgZone, AfterViewChecked, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import type { PromptSelectionComponent } from '../prompt-selection/prompt-selection';
import { EXTERNAL_IMPORTS } from '~/classes/imports/external.imports';
import { APP_IMPORTS } from '~/classes/imports/app.imports';
import jsPDF from 'jspdf'

import { SIDEBAR_SECTIONS, LEAF_NAV_MAP } from './data/sidebar-config';

import { WellDataApiService } from '~/services/well-data-api.service';
import { QueryApiService } from '~/services/query-api.service';
import { WebSocketService, WsMessage } from '~/services/websocket.service';
import { SessionService } from '~/services/session.service';
import { AppSettingsService } from '~/core/services/appsettings.service';
import { AppConfigService } from '~/core/services/appconfig.service';
import { GraphService } from '~/core/services/graph.service';
import { HttpService } from '~/core/services/http.service';
import { MsalService } from '@azure/msal-angular';
import { forkJoin, Subscription, timer, lastValueFrom } from 'rxjs';
import { retryWhen, delay, take, mergeMap } from 'rxjs/operators';

// Highcharts
import * as Highcharts from 'highcharts';
import 'highcharts/modules/exporting';
import 'highcharts/modules/offline-exporting';
import { HighchartsChartModule } from 'highcharts-angular';
// Modules auto-compose onto Highcharts in v12 — no factory call needed


@Component({
  selector: 'app-wells-pa-insights',
  standalone: true,
  imports: [
    ...EXTERNAL_IMPORTS,
    ...APP_IMPORTS,
    HighchartsChartModule
  ],
  templateUrl: './wells-pa-insights.html',
  styleUrls: ['./wells-pa-insights.scss', './wells-pa-insights.css']
})
export class WellsPaInsightsComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('centerRef') centerRef!: ElementRef;
  @ViewChild('promptSelectionView') promptSelectionView: PromptSelectionComponent | undefined;
  @ViewChild('fieldChatMessages') fieldChatMessages: ElementRef | undefined;
  @ViewChild('wellChatMessages') wellChatMessages: ElementRef | undefined;
  @ViewChild('promptChatMessages') promptChatMessages: ElementRef | undefined;

  // Expose Math to template
  Math = Math;
  // Expose Array to template for Array.isArray checks
  Array = Array;
  // Highcharts reference for template
  Highcharts: typeof Highcharts = Highcharts;

  private wsSub: Subscription | null = null;  // WebSocket message subscription
  private shouldScroll = false;  // Flag to trigger auto-scroll on next AfterViewChecked
  private _navigatingFromHistory = false; // Guard: prevents paramMap from overwriting history-loaded messages
  private _lastRouteKey = '';
  private _lastConversationsLoadAt = 0;

  constructor(
   
    private wellDataApi: WellDataApiService,
    private queryApi: QueryApiService,
    private wsService: WebSocketService,
    private sessionService: SessionService,
    private httpService: HttpService,
    private route: ActivatedRoute,
    private router: Router,
    private appSettingsService: AppSettingsService,
    private appConfigService: AppConfigService,
    private graphService: GraphService,
    private msalService: MsalService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private sanitizer: DomSanitizer,
  ) {}

  // Sign the user out via MSAL and redirect to the access denied page.
  // This ensures users who authenticated but are not in the required AD group
  // do not remain 'logged in' in the client.
  private signOutAndRedirectToAccess(): void {
    try {
      const redirectUri = window.location.origin + '/access';
      // Use MSAL's logoutRedirect to clear the session
      this.msalService.logoutRedirect({ postLogoutRedirectUri: redirectUri } as any);
    } catch (err) {
      // Fallback: client-side redirect to access page
      this.router.navigate(['/access'], { state: { type: 'application' } });
    }
  }


  ngOnInit() {
    // Initialize screen based on current URL
    const currentUrl = this.router.url;
    const isRoot = currentUrl === '/';
    const isPrompt = currentUrl === '/prompt' || currentUrl.startsWith('/prompt?');
    const isWellRoute = currentUrl.startsWith('/well/');
    const isFieldRoute = currentUrl.startsWith('/field/');

    // Set initial screen state based on URL
    if (isRoot) {
      this.screen = 'landing';
      this.activeNav = 'home';
    } else if (isPrompt) {
      this.screen = 'app';
      this.activeNav = 'home';
    } else if (isWellRoute || isFieldRoute) {
      this.screen = 'app';
    } else {
      this.screen = 'landing';
      this.activeNav = 'home';
    }

  // Do NOT connect WebSocket on launch.
  // WebSocket should connect only when user sends first question.

    if (isPrompt) {
      // Only process /prompt route, don't auto-connect WS here
      // AuthGuard will handle auth verification
      // User explicitly navigates here via arrow click
      this.checkMetricsAccess();
      return;
    }

    if (isWellRoute || isFieldRoute) {
      // For well/field routes, restore state only.
      // No auto-session and no auto-WebSocket connect here.
      this.setupRouteSubscriptions();
      this.setupWindowResizeListener();
      this.checkMetricsAccess();
      return;
    }

    // Landing page - no WebSocket connection needed
    this.setupRouteSubscriptions();
    this.setupWindowResizeListener();
    
    // Check if user has access to metrics dashboard and RCA table
    this.checkMetricsAccess();
  }

  private setupRouteSubscriptions(): void {
    // Subscribe to route changes for dynamic routing
    this.route.url.subscribe((urlSegments: any) => {
      const path = urlSegments.map((s: any) => s.path).join('/');
      if (path === '') {
        this.screen = 'landing';
        this.activeNav = 'home';
      }
    });

    // Subscribe to param changes for well/field/multiwell routes
    this.route.paramMap.subscribe((params: any) => {
      const sessionId = params.get('sessionId');
      const wellId = params.get('wellId');
      const routeKey = `${wellId || 'multi'}:${sessionId || ''}`;

      // Angular route streams can emit duplicate values; ignore exact duplicates.
      if (routeKey === this._lastRouteKey) {
        return;
      }
      this._lastRouteKey = routeKey;

      if (wellId && sessionId) {
        // Well-level route: /well/:wellId/:sessionId
        this.screen = 'app';
        this.currentSessionId = sessionId;
        
        // Load messages from API to include any feedback that was just submitted
        this.chatLoading = true;
        
        // First, check if we have fresh data in sessionStorage (for clarifications and immediate messages)
        const storedDataWell = sessionStorage.getItem(`query_response_${sessionId}`);
        if (storedDataWell) {
          try {
            const data = JSON.parse(storedDataWell);
            if (data.messages && Array.isArray(data.messages) && data.messages.length > 0) {
              this.messages = data.messages;
              this.lastQueryResponse = data.queryResponse;
              this.chatLoading = false;
              this.shouldScroll = true;
              setTimeout(() => this.scrollChatToBottom(this.wellChatMessages), 300);
              this.loadWell(wellId, true);
              this.activeNav = 'overview';
              this.selectedSection = 'overview';
              this.selectedSubTab = 'wells_summary';
              this.showDataQualityPopover = false;
              this.loadConversationsFromApi();
              return;
            }
          } catch (e) {
            throw e
          }
        }
        
        // If no sessionStorage data, load from API
        this.queryApi.getConversationById(sessionId).subscribe({
          next: (detail: any) => {
            const rawMessages = detail.messages || detail.chat_history || detail.conversation || [];
            const normalizeRole = (role: string): 'user' | 'assistant' => {
              const r = (role || '').toLowerCase();
              if (r === 'user' || r === 'human') return 'user';
              return 'assistant';
            };
            
            const parsedMessages = rawMessages.map((m: any) => {
              const role = normalizeRole(m.role || m.type || 'assistant');
              const content = m.content || m.text || m.message || '';
              const msg: any = { role, content, message_id: m.message_id || '' };
              if (role === 'assistant') {
                if (Array.isArray(m.results) && m.results.length > 0) {
                  msg.results = m.results;
                }
                if (m.visualization) {
                  msg.visualization = m.visualization;
                  msg.chartOptions = this.buildChartOptions(m.results || [], m.visualization);
                  msg.viewMode = 'table';
                }
                // Map feedback sentiment to feedbackType
                if (m.feedback && m.feedback.sentiment) {
                  msg.feedbackType = m.feedback.sentiment === 'Like' ? 'like' : 'dislike';
                }
              }
              return msg;
            }).filter((m: any) => m.content);
            
            this.messages = parsedMessages;
            this.chatLoading = false;
            this.shouldScroll = true;
            setTimeout(() => this.scrollChatToBottom(this.wellChatMessages), 300);
            this.loadWell(wellId, true);
            this.activeNav = 'overview';
            this.selectedSection = 'overview';
            this.selectedSubTab = 'wells_summary';
            this.showDataQualityPopover = false;
            this.loadConversationsFromApi();
          },
          error: (err) => {
            // Fallback to sessionStorage if API fails
            const stored = sessionStorage.getItem(`query_response_${sessionId}`);
            if (stored) {
              try {
                const data = JSON.parse(stored);
                this.lastQueryResponse = data.queryResponse;
                if (data.messages && Array.isArray(data.messages)) {
                  this.messages = data.messages;
                  this.chatLoading = false;
                  this.shouldScroll = true;
                  setTimeout(() => this.scrollChatToBottom(this.wellChatMessages), 300);
                }
              } catch (e) {
                throw e
              }
            }
            this.chatLoading = false;
            this.loadWell(wellId, true);
            this.activeNav = 'overview';
            this.selectedSection = 'overview';
            this.selectedSubTab = 'wells_summary';
            this.showDataQualityPopover = false;
            this.loadConversationsFromApi();
          }
        });
      } else if (sessionId && !wellId) {
        // Check if this is a multiwell or field route by examining the URL path
        this.route.url.pipe(take(1)).subscribe((urlSegments: any) => {
          const path = urlSegments.map((s: any) => s.path).join('/');
          const isMultiwell = path.startsWith('multiwell');
          
          this.screen = 'app';
          this.currentSessionId = sessionId;
          
          if (isMultiwell) {
            // Multi-well route: /multiwell/:sessionId
            this.isMultiWellMode = true;
            this.activeNav = 'overview';
            this.selectedSection = 'overview';
            this.selectedSubTab = 'wells_summary';
            
            // Restore selected wells from sessionStorage
            const storedMultiwell = sessionStorage.getItem(`multiwell_selected_${sessionId}`);
            if (storedMultiwell) {
              try {
                const data = JSON.parse(storedMultiwell);
                if (data.selectedWells && data.selectedWells.length > 0) {
                  this.fieldWellIds = data.selectedWells;
                  this.loadFieldData(data.selectedWells);
                }
              } catch (e) {
                throw e;
              }
            }
          } else {
            // Field-level route: /field/:sessionId
            this.isMultiWellMode = false;
            this.activeNav = 'field';
          }
          
          // Load messages from API to include any feedback that was just submitted
          this.chatLoading = true;
          
          // First, check if we have fresh data in sessionStorage (for clarifications and immediate messages)
          const storedData = sessionStorage.getItem(`query_response_${sessionId}`);
          if (storedData) {
            try {
              const data = JSON.parse(storedData);
              if (data.messages && Array.isArray(data.messages) && data.messages.length > 0) {
                this.messages = data.messages;
                this.lastQueryResponse = data.queryResponse;
                this.chatLoading = false;
                this.shouldScroll = true;
                const chatRef = isMultiwell ? this.fieldChatMessages : this.fieldChatMessages;
                setTimeout(() => this.scrollChatToBottom(chatRef), 300);
                // Load chat history even when using sessionStorage
                this.loadConversationsFromApi();
                return;
              }
            } catch (e) {
              throw e;
            }
          }
          
          // If no sessionStorage data, load from API
          this.queryApi.getConversationById(sessionId).subscribe({
            next: (detail: any) => {
              const rawMessages = detail.messages || detail.chat_history || detail.conversation || [];
              const normalizeRole = (role: string): 'user' | 'assistant' => {
                const r = (role || '').toLowerCase();
                if (r === 'user' || r === 'human') return 'user';
                return 'assistant';
              };
              
              const parsedMessages = rawMessages.map((m: any) => {
                const role = normalizeRole(m.role || m.type || 'assistant');
                const content = m.content || m.text || m.message || '';
                const msg: any = { role, content, message_id: m.message_id || '' };
                if (role === 'assistant') {
                  if (Array.isArray(m.results) && m.results.length > 0) {
                    msg.results = m.results;
                  }
                  if (m.visualization) {
                    msg.visualization = m.visualization;
                    msg.chartOptions = this.buildChartOptions(m.results || [], m.visualization);
                    msg.viewMode = 'table';
                  }
                  // Map feedback sentiment to feedbackType
                  if (m.feedback && m.feedback.sentiment) {
                    msg.feedbackType = m.feedback.sentiment === 'Like' ? 'like' : 'dislike';
                  }
                }
                return msg;
              }).filter((m: any) => m.content);
              
              this.messages = parsedMessages;
              this.lastQueryResponse = detail;
              this.chatLoading = false;
              this.shouldScroll = true;
              const chatRef = isMultiwell ? this.fieldChatMessages : this.fieldChatMessages;
              setTimeout(() => this.scrollChatToBottom(chatRef), 300);
            },
            error: (err) => {
              // Fallback to sessionStorage if API fails
              const stored = sessionStorage.getItem(`query_response_${sessionId}`);
              if (stored) {
                try {
                  const data = JSON.parse(stored);
                  this.lastQueryResponse = data.queryResponse;
                  if (data.messages && Array.isArray(data.messages)) {
                    this.messages = data.messages;
                    this.chatLoading = false;
                    this.shouldScroll = true;
                    const chatRef = isMultiwell ? this.fieldChatMessages : this.fieldChatMessages;
                    setTimeout(() => this.scrollChatToBottom(chatRef), 300);
                  }
                } catch (e) {
                  throw e;
              } }
              this.chatLoading = false;
            }
          });
          
          // Load conversations when entering dashboard page
          this.loadConversationsFromApi();
        });
      }
    });
  }

  ngOnDestroy() {
    this.wsSub?.unsubscribe();
    // Do not disconnect WebSocket here—keep it alive across the app
  }

  ngAfterViewChecked() {
    // Auto-scroll to bottom when messages change
    if (this.shouldScroll) {
      // Try field chat container first
      if (this.fieldChatMessages) {
        this.scrollChatToBottom(this.fieldChatMessages);
      } else if (this.wellChatMessages) {
        this.scrollChatToBottom(this.wellChatMessages);
      } else if (this.promptChatMessages) {
        this.scrollChatToBottom(this.promptChatMessages);
      }
      this.shouldScroll = false;
    }
  }

  private scrollChatToBottom(ref?: ElementRef) {
    setTimeout(() => {
      const el = (ref || this.fieldChatMessages || this.wellChatMessages || this.promptChatMessages)?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    }, 150);
  }

  /** Called by arrow button on landing page. Navigates to /prompt. */
  openApp() {
    // Navigate to /prompt - AuthGuard will handle authentication/authorization
    // If authorized, this navigates to /prompt and waits for first question.
    // If not authorized, AuthGuard redirects to /access
    this.router.navigate(['/prompt']);
  }

  /** Open mobile chat popup */
  openMobileChat() {
    this.mobileChatOpen = true;
    this.cdr.markForCheck();
    setTimeout(() => this.cdr.detectChanges(), 0);
  }

  // ── WebSocket helpers ────────────────────────────────────────────────────

  /**
   * Connects WebSocket for the current session and subscribes to messages.
   * Re-uses the connection if already open for the same session.
   */
  private connectAndSubscribeWs(sessionId: string): void {
    // Always unsubscribe previous listener to avoid stale handlers
    this.wsSub?.unsubscribe();
    this.wsSub = null;

    // Only reconnect WebSocket if not already connected to this session
    this.wsService.connect(sessionId);

    // Always create a fresh subscription so the handler captures current component state
    this.wsSub = this.wsService.messages$.subscribe(
      (msg: WsMessage) => {
        // Run all message handling inside Angular's zone to ensure UI updates
        this.ngZone.run(() => {
          this.handleWsMessage(msg);
        });
      },
      (error) => {
        // Handle WebSocket connection errors
        const errorMsg = error?.message || 'Connection lost. Please try again.';
        const errorContent = `⚠️ Connection Error: ${errorMsg}`;

        if (this.promptHome) {
          this.showPromptPageError(errorContent);
        } else {
          this.chatStatus = '';
          this.chatLoading = false;
          this.loading = false;
          this.messages = [...this.messages, {
            role: 'assistant',
            content: errorContent,
            isError: true
          }];
          this.shouldScroll = true;
          this.cdr.detectChanges();
        }
      }
    );
  }

  /**
   * Central handler for every WebSocket message.
   * Updates chat UI progressively as streaming events arrive.
   */
  private handleWsMessage(msg: WsMessage): void {
    switch (msg.type) {
      case 'status':
        this.chatStatus = msg.data;
        // Trigger change detection to update status text
        this.cdr.detectChanges();
        break;

      case 'sql':
        // Optionally display SQL in chat (stored but not shown by default)
        this.lastQueryResponse = { ...this.lastQueryResponse, sql: msg.data };
        break;

      case 'answer':
        // Push the AI-generated answer into chat
        this.messages = [...this.messages, { role: 'assistant', content: msg.data }];
        this.chatStatus = '';
        this.shouldScroll = true;  // Trigger auto-scroll
        // Trigger change detection to update UI immediately
        this.cdr.detectChanges();
        break;

      case 'complete':
        // Final payload with results, entities, timing

        // Check if the complete event contains a clarification type response
        if (msg.data && msg.data.type === 'clarification') {
          this.chatStatus = '';
          this.chatLoading = false;
          this.loading = false;
          this.hasNavigatedForFirstQuestion = true; // don't navigate on clarification
          const clarificationContent = typeof msg.data.data === 'string'
            ? msg.data.data
            : (msg.data.message || msg.data.data || 'Could you please clarify your question?');
          this.messages = [...this.messages, {
            role: 'assistant',
            content: clarificationContent,
            responseType: 'clarification'
          }];
          this.shouldScroll = true;

          // IMPORTANT: Save to sessionStorage BEFORE navigation to ensure messages persist
          this.saveSessionToStorage();
          
          // Trigger change detection to render the message immediately
          this.cdr.markForCheck();
          this.cdr.detectChanges();
          
          // Navigate to field view to display the clarification message in chat panel
          if (this.promptHome) {
            this.activeNav = 'field';
            // Defer navigation to next microtask to ensure sessionStorage is saved first
            Promise.resolve().then(() => {
              this.router.navigate(['/field', this.currentSessionId]);
            });
          }
          
          break;
        }

        this.chatStatus = '';
        this.chatLoading = false;
        this.loading = false;
        this.lastQueryResponse = { ...this.lastQueryResponse, ...msg.data };

        // Always attach message_id and session_id to the most recent assistant message for feedback API
        const lastMsgIdx = this.messages.length - 1;
        if (lastMsgIdx >= 0 && this.messages[lastMsgIdx].role === 'assistant') {
          const lastMsg = this.messages[lastMsgIdx];
          // Create updated message with metadata
          const updatedMsg: any = { 
            ...lastMsg,
            message_id: msg.data.message_id,  // Attach message_id for feedback API
            session_id: msg.data.session_id   // Also attach session_id for reference
          };

          // Attach results array if present
          if (Array.isArray(msg.data?.results) && msg.data.results.length > 0) {
            updatedMsg.results = msg.data.results;
            // Attach visualization config if present
            if (msg.data.visualization) {
              updatedMsg.visualization = msg.data.visualization;
              updatedMsg.chartOptions = this.buildChartOptions(msg.data.results, msg.data.visualization);
              updatedMsg.viewMode = 'table'; // default to table view
            }
            // Reset pagination when new results arrive
            this.resetResultsPagination();
          }

          // Replace the message in the array
          const newMessages = [...this.messages];
          newMessages[lastMsgIdx] = updatedMsg;
          this.messages = newMessages;
        }

        this.shouldScroll = true;  // Trigger auto-scroll
        // Force change detection multiple times to ensure UI updates
        this.cdr.markForCheck();
        setTimeout(() => this.cdr.detectChanges(), 0);

        // Persist to sessionStorage BEFORE navigation
        this.saveSessionToStorage();
        this.pushChatHistoryEntry(this.messages, this.messages[0]?.content || '');

        // Defer navigation to allow UI to render first
        // Use Promise.resolve().then() to push navigation to next microtask
        Promise.resolve().then(() => {
          // Extract well IDs and navigate (ONLY for the first question in this session)
          if (!this.hasNavigatedForFirstQuestion) {
            const wellIds = this.extractWellIdsFromResponse(msg.data);
            
            // Mark that we've done the first navigation
            this.hasNavigatedForFirstQuestion = true;
            
            if (wellIds.length === 1) {
              // Single well → Navigate to well-level dashboard
              this.isMultiWellMode = false;
              this.router.navigate(['/well', wellIds[0], this.currentSessionId]);
              // Do not call loadWell() here; route param subscription will load once.
            } else if (wellIds.length > 1) {
              // Multiple wells → Navigate to field view
              this.isMultiWellMode = true;
              this.fieldWellIds = wellIds;
              this.router.navigate(['/field', this.currentSessionId]);
              this.loadFieldData(wellIds);
            } else {
              // No wells found → Navigate to field view (general/field-level insights)
              this.isMultiWellMode = false;
              this.fieldWellIds = [];
              this.router.navigate(['/field', this.currentSessionId]);
            }
          } 
        });
        break;

      case 'error':
        // Show error message with proper formatting
        const errorMsg = msg.data || 'An error occurred. Please try again.';
        if (this.promptHome) {
          this.showPromptPageError(`⚠️ ${errorMsg}`);
        } else {
          this.chatStatus = '';
          this.chatLoading = false;
          this.loading = false;
          this.messages = [...this.messages, {
            role: 'assistant',
            content: errorMsg,
            isError: true
          }];
          this.shouldScroll = true;  // Trigger auto-scroll
          this.cdr.detectChanges();
          this.saveSessionToStorage();
        }
        break;

      case 'auth_failed':
        // Show authentication error message
        const authErrorMsg = msg.data || 'Authentication failed. Please try again.';
        if (this.promptHome) {
          this.showPromptPageError(`🔒 Authentication Error: ${authErrorMsg}`);
        } else {
          this.chatStatus = '';
          this.chatLoading = false;
          this.loading = false;
          this.messages = [...this.messages, {
            role: 'assistant',
            content: `🔒 Authentication Error`,
            isError: true
          }];
          this.shouldScroll = true;  // Trigger auto-scroll
          this.cdr.markForCheck();
          setTimeout(() => this.cdr.detectChanges(), 0);
          this.saveSessionToStorage();
        }
        break;

      case 'clarification':
        // Backend needs more info — show the clarification question as an assistant message
        // so the user can read it and reply in the same chat thread
        this.chatStatus = '';
        this.chatLoading = false;
        this.loading = false;
        this.hasNavigatedForFirstQuestion = true; // don't navigate on clarification
        const clarificationText = typeof msg.data === 'string'
          ? msg.data
          : (msg.data?.message || msg.data?.data || 'Could you please clarify your question?');
        this.messages = [...this.messages, {
          role: 'assistant',
          content: clarificationText,
          responseType: 'clarification'
        }];
        this.shouldScroll = true;

        // IMPORTANT: Save to sessionStorage BEFORE navigation to ensure messages persist
        this.saveSessionToStorage();
        
        // Trigger change detection to render the message immediately
        this.cdr.markForCheck();
        this.cdr.detectChanges();
        
        // Navigate to field view to display the clarification message in chat panel
        if (this.promptHome) {
          this.activeNav = 'field';
          // Defer navigation to next microtask to ensure sessionStorage is saved first
          Promise.resolve().then(() => {
            this.router.navigate(['/field', this.currentSessionId]);
          });
        }
        
        break;

      default:
        break;
    }
  }

  /** Persist current messages + query response to sessionStorage */
  private saveSessionToStorage(): void {
    if (!this.currentSessionId) return;
    sessionStorage.setItem(`query_response_${this.currentSessionId}`, JSON.stringify({
      queryResponse: this.lastQueryResponse,
      messages: this.messages,
      timestamp: new Date().toISOString()
    }));
  }

  /**
   * Resets the prompt page to an error state — clears loading spinner and shows error banner.
   * Called whenever an error occurs while still on the prompt home page.
   */
  private showPromptPageError(message: string): void {
    this.chatLoading = false;
    this.loading = false;
    this.chatStatus = '';
    this.promptPageError = message;
    this.promptSelectionView?.resetLoading();
    this.cdr.detectChanges();
  }

  /**
   * Called when user selects a prompt (from PromptSelectionComponent).
   * Opens a WebSocket, sends the question, and streams the response into the chat.
   */
  async onPromptSelected(event: { text: string; type: string; promptId?: string; selectedWells?: string[] }) {
    this.promptPageError = null;  // Clear any previous error
    
    // Check if this is a multi-well prompt with selected wells
    if (event.selectedWells && event.selectedWells.length > 0 && event.type === 'multi-well') {
      this.isMultiWellMode = true;
      this.fieldWellIds = event.selectedWells;
      this.multiWellIdsSentForSession = false; // Reset flag for new multiwell session
      
      // Generate session ID for multi-well mode
      const sessionId = QueryApiService.generateSessionId();
      this.currentSessionId = sessionId;
      
      // Store selected wells and session info for page refresh
      sessionStorage.setItem(`multiwell_selected_${sessionId}`, JSON.stringify({
        selectedWells: event.selectedWells,
        timestamp: new Date().toISOString()
      }));
      
      // Store welcome message for multi-well mode
      const welcomeMessage = [
        { role: 'assistant', content: 'Ask a question to start a conversation about the selected wells.', isWelcomeMessage: true }
      ];
      sessionStorage.setItem(`query_response_${sessionId}`, JSON.stringify({
        queryResponse: null,
        messages: welcomeMessage
      }));
      
      // Load field data for selected wells
      this.loadFieldData(event.selectedWells);
      
      // Navigate to multi-well dashboard with session ID
      this.router.navigate(['/multiwell', sessionId]);
      return;
    }

    this.loading = true;
    this.chatLoading = true;
    this.chatStatus = 'Connecting…';
    
    // Ensure user is authenticated before opening WebSocket
    const accounts = this.msalService.instance.getAllAccounts();
    if (!accounts || accounts.length === 0) {
      // Redirect to access page — do not allow prompt to be submitted
      this.router.navigate(['/access'], { state: { type: 'application' } });
      return;
    }

    // Check AD group membership if configured (optional)
    const requiredGroupObjectId = this.appConfigService.getConfig().object_id;
    if (requiredGroupObjectId && requiredGroupObjectId.trim() !== '') {
      try {
        const isMember = await this.graphService.isUserInRequiredGroup(requiredGroupObjectId);
        if (!isMember) {
          this.router.navigate(['/access'], { state: { type: 'application' } });
          return;
        }
      } catch (e) {
        this.router.navigate(['/access'], { state: { type: 'application' } });
        return;
      }
    }
    
    const sessionId = QueryApiService.generateSessionId();
    this.currentSessionId = sessionId;

    // Reset navigation flag for new session - this ensures first question in new session will trigger navigation
    this.hasNavigatedForFirstQuestion = false;

    // Add user message immediately
    this.messages = [{ role: 'user', content: event.text }];
    this.shouldScroll = true;  // Trigger auto-scroll
    this.cdr.markForCheck();

    // Set a timeout to prevent infinite loading if WebSocket doesn't respond
    const loadingTimeout = setTimeout(() => {
      if (this.chatLoading) {
        const timeoutMsg = '⚠️ Request timeout. The server did not respond. Please try again.';
        if (this.promptHome) {
          this.showPromptPageError(timeoutMsg);
        } else {
          this.chatLoading = false;
          this.loading = false;
          this.chatStatus = '';
          this.messages = [...this.messages, {
            role: 'assistant',
            content: timeoutMsg,
            isError: true
          }];
          this.cdr.detectChanges();
        }
      }
    }, 30000); // 30 second timeout

    // Connect WebSocket and send question
    this.connectAndSubscribeWs(sessionId);

    // Wait a tiny bit for the socket to open, then send
    const trySend = () => {
      if (this.wsService.isConnected) {
        clearTimeout(loadingTimeout); // Clear timeout when connection succeeds
        this.chatStatus = 'Processing…';
        this.wsService.sendQuestion(event.text);
      } else {
        setTimeout(trySend, 200);
      }
    };
    trySend();
  }

  /**
   * Extracts well_ids from the API query response.
   * Expected format: { well_ids: string[] } or { well_id: string }
   */
  private extractWellIdsFromResponse(res: any): string[] {
    // Try entities.resolved_well first (preferred from query API)
    if (res.entities?.resolved_well && Array.isArray(res.entities.resolved_well)) {
      return res.entities.resolved_well.map((w: any) => w.well_id).filter((id: string) => id);
    }
    // Fallback to well_ids
    if (res.well_ids && Array.isArray(res.well_ids)) {
      return res.well_ids;
    }
    // Fallback to single well_id
    if (res.well_id && typeof res.well_id === 'string') {
      return [res.well_id];
    }
    // No wells found
    return [];
  }

  /**
   * Loads well summary data from multiple well IDs for multi-well mode.
   * Stores both well names and full summary data.
   * Other APIs are loaded on-demand per tab.
   */
  private loadFieldData(wellIds: string[]) {
    if (!wellIds || wellIds.length === 0) {
      this.loading = false;
      return;
    }

    this.fieldWellIds = wellIds;
    this.fieldWellNames = [];
    this.fieldAggregatedNptEvents = [];
    this.fieldAggregatedNptData = [];
    
    // Clear all cached API data for multi-well when new wells are selected
    this.apiNptSummary = null;
    this.apiNptLoading = false;
    this.apiNptError = null;
    this.apiPerformance = null;
    this.apiPerformanceLoading = false;
    this.apiPerformanceError = null;
    this.apiTimeLog = [];
    this.apiTimeLogLoading = false;
    this.apiTimeLogError = null;
    
    this.loading = true;

    // Load well summary data for multi-well
    this.apiWellSummaryLoading = true;
    this.apiWellSummaryError = null;
    
    this.wellDataApi.getWellsSummary(wellIds).subscribe({
      next: (response: any) => {
        const summaryData = response?.data || [];
        const wellNames: string[] = [];
        
        // Create a mapping from well ID to well name for correct lookup
        this.wellIdToNameMap.clear();

        // Extract well names and store full summary data
        summaryData.forEach((well: any) => {
          if (well.WellName) {
            wellNames.push(well.WellName);
            // Map well ID to name for quick lookup
            this.wellIdToNameMap.set(well.WellID, well.WellName);
          }
        });

        this.fieldWellNames = wellNames;
        // Store multi-well summary separately (as array)
        this.multiWellSummaryData = summaryData;
        this.apiWellSummaryLoading = false;
        this.apiWellSummaryError = null;
        this.loading = false;
        
        // Reset prompt component loading state
        if (this.promptSelectionView && typeof this.promptSelectionView.resetLoading === 'function') {
          this.promptSelectionView.resetLoading();
        }
        
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.apiWellSummaryError = 'Failed to load well summary. Please try again.';
        this.apiWellSummaryLoading = false;
        this.loading = false;
        
        // Reset prompt component loading state even on error
        if (this.promptSelectionView && typeof this.promptSelectionView.resetLoading === 'function') {
          this.promptSelectionView.resetLoading();
        }
        
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Load NPT summary data for multi-well mode (called when user opens NPT Summary tab)
   */
  private loadMultiWellNptData() {
    if (!this.isMultiWellMode || !this.fieldWellIds || this.fieldWellIds.length === 0) return;
    if (this.apiNptLoading || this.apiNptSummary) return; // Already loading or loaded

    this.apiNptLoading = true;
    this.apiNptError = null;
    this.wellDataApi.getNptSummary(this.fieldWellIds).subscribe({
      next: (response: any) => {
        const nptData = response?.data || [];
        const allNptEvents: any[] = [];
        const catMap: Record<string, { name: string; hours: number; cost: number }> = {};

        // Process NPT data
        nptData.forEach((well: any) => {
          const wellId = well.WellID;
          const wellName = well.WellName || wellId;

          if (well.NptEvents && Array.isArray(well.NptEvents)) {
            allNptEvents.push(...well.NptEvents.map((e: any) => ({
              wellId,
              wellName,
              category: e.MajorCategory || 'OTHER',
              driver: e.MajorCategory || 'OTHER',
              detail: e.Detail,
              description: e.Description,
              duration: e.NetProblemDuration || 0,
              cost: e.ProblemCost || 0,
              startDate: e.StartDate,
              endDate: e.EndDate,
              serviceContractor: e.ServiceContractors || '',
            })));
          }
        });

        // If no detailed events, create summary events from TotalNptCost (for aggregation purposes)
        if (allNptEvents.length === 0) {
          nptData.forEach((well: any) => {
            if (well.TotalNptCost > 0 || well.TotalNptHours > 0) {
              allNptEvents.push({
                wellId: well.WellID,
                wellName: well.WellName || well.WellID,
                category: 'NPT',
                driver: 'NPT',
                detail: '',
                description: 'Total NPT for well',
                duration: well.TotalNptHours || 0,
                cost: well.TotalNptCost || 0,
                startDate: null,
                endDate: null,
                serviceContractor: '',
              });
            }
          });
        }

        // Aggregate by category
        for (const event of allNptEvents) {
          const cat = event.category;
          if (!catMap[cat]) {
            catMap[cat] = { name: cat, hours: 0, cost: 0 };
          }
          catMap[cat].hours += event.duration;
          catMap[cat].cost += event.cost;
        }

        this.apiNptSummary = response.data;
        this.fieldAggregatedNptEvents = allNptEvents;
        this.fieldAggregatedNptData = Object.values(catMap).sort((a: any, b: any) => b.hours - a.hours);
        this.apiNptError = null;
        this.apiNptLoading = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.apiNptError = 'Failed to load NPT summary. Please try again.';
        this.apiNptLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Load performance data for multi-well mode (called when user opens Cost/Performance tab)
   */
  private loadMultiWellPerformanceData() {
    if (!this.isMultiWellMode || !this.fieldWellIds || this.fieldWellIds.length === 0) return;
    if (this.apiPerformanceLoading || this.apiPerformance) return; // Already loading or loaded

    this.apiPerformanceLoading = true;
    this.apiPerformanceError = null;
    this.wellDataApi.getPerformanceSummary(this.fieldWellIds).subscribe({
      next: (response: any) => {
        this.apiPerformance = response.data;
        this.apiPerformanceError = null;
        this.apiPerformanceLoading = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.apiPerformanceError = 'Failed to load performance data. Please try again.';
        this.apiPerformanceLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Load causal analysis data for multi-well mode (called when user opens NPT Summary tab)
   */
  private loadMultiWellCausalAnalysis() {
    if (!this.isMultiWellMode || !this.fieldWellIds || this.fieldWellIds.length === 0) return;
    if (this.apiRcaLoading || this.apiRcaComplete) return; // Already loading or loaded

    this.apiRcaLoading = true;
    this.apiRcaData = [];
    this.apiRcaComplete = false;

    // Load causal analysis for all wells
    this.wellDataApi.getCausalSummary(this.fieldWellIds).subscribe({
      next: (response: any) => {
        const data = response?.data || [];
        const allCauses: any[] = [];

        // Process causal analysis for each well
        data.forEach((wellData: any) => {
          const wellName = wellData.WellName || wellData.WellID || '';
          const causes = Array.isArray(wellData.causes) ? wellData.causes : (Array.isArray(wellData.CausalAnalysis) ? wellData.CausalAnalysis : []);
          
          causes.forEach((c: any) => {
            allCauses.push({
              wellName: wellName,
              nptEvent: c.Description || '',
              rootCause: (c.RootCause || '').split('\n').map((l: string) => l.trim()).join('\n'),
              confidence: c.ConfidenceScore || 0,
              evidence: c.Evidence || '',
              eventId: c.EventID || '',
              jobId: c.JobID || '',
              VerificationStatus: c.VerificationStatus || null,
              VerifiedBy: c.VerifiedBy || null,
            });
          });
        });

        this.apiRcaData = allCauses;
        this.apiRcaComplete = true;
        this.apiRcaLoading = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.apiRcaLoading = false;
        this.apiRcaComplete = false;
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Load time log data for multi-well mode (called when user opens time log)
   */
  /**
   * Load timelog for a selected well in multi-well mode
   * @param selectedWellId - The WellID of the selected well
   */
  loadMultiWellTimeLog(selectedWellId?: string) {
    if (!this.isMultiWellMode || !this.fieldWellIds || this.fieldWellIds.length === 0) return;
    if (this.apiTimeLog.length || this.apiTimeLogLoading) return; // Already loaded or loading

    // If no specific well selected, cannot proceed
    if (!selectedWellId) {
      this.apiTimeLogError = 'Please select a well to view timelog.';
      return;
    }

    this.apiTimeLogLoading = true;
    this.apiTimeLogError = null;

    // Extract JobID from NPT summary for the selected well
    const wellNptData = this.apiNptSummary?.find((well: any) => well.WellID === selectedWellId);
    const jobId = wellNptData?.JobID;

    if (!jobId) {
      this.apiTimeLogLoading = false;
      this.apiTimeLogError = 'JobID not found for selected well. Please ensure NPT data is loaded.';
      this.cdr.markForCheck();
      return;
    }

    // Call time log API with selected well ID and its JobID
    this.wellDataApi.getTimeLog(selectedWellId, jobId).subscribe({
      next: (response: any) => {
        // Filter response to only include data for the selected well
        let timeLogData = response?.data;
        if (Array.isArray(timeLogData)) {
          timeLogData = timeLogData.find((item: any) => item.WellID === selectedWellId);
        }
        
        this.apiTimeLog = (timeLogData?.timeLogEntries || []).map((e: any) => ({
          activityCode: e.activityCode,
          comment:      e.comment,
          startDate:    e.startDate,
          endDate:      e.endDate,
        }));
        this.apiTimeLogError = null;
        this.apiTimeLogLoading = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.apiTimeLogError = 'Failed to load time log data. Please try again.';
        this.apiTimeLog = [];
        this.apiTimeLogLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  // Public method to load conversations - called from prompt selection page
  public loadConversations() {
    this.loadConversationsFromApi();
  }

  private loadConversationsFromApi() {
    if (this.loadingHistory) return;

    const now = Date.now();
    // Prevent burst reloads from repeated route/subscription emissions.
    if (now - this._lastConversationsLoadAt < 8000) {
      return;
    }
    this._lastConversationsLoadAt = now;

    this.loadingHistory = true;
    this.queryApi.getConversations().pipe(
      retryWhen(errors => errors.pipe(
        mergeMap((err, attempt) => {
          // Retry up to 3 times with 1s delay — gives MSAL time to restore account after page refresh
          if (attempt < 3) {
            return timer(1000);
          }
          throw err;
        })
      ))
    ).subscribe({
      next: (response: any) => {
        // Handle both array and object with sessions property
        const sessions = Array.isArray(response) ? response : (response?.sessions || []);
        
        
        // Clear local chatHistory before loading from API to avoid duplicates
        this.chatHistory = [];
        const seenIds = new Set<string>();
        this.chatHistory = sessions.map((s: any) => {
          // Only add if not already present
          if (seenIds.has(s.session_id)) return null;
          seenIds.add(s.session_id);
          return {
            id: s.session_id,
            question: s.description || s.session_id,
            answer: '',
            section: 'overview',
            subTab: 'wells_summary',
            fieldPromptId: '',
            wellId: s.well_id || '',
            timestamp: new Date(s.created_at).toLocaleString(),
            messages: [],
            session_id: s.session_id,
          };
        }).filter(Boolean);
        
        this.loadingHistory = false;
        this.cdr.detectChanges();
      },
      error: (err) => { 
        this.loadingHistory = false;
        /* keep local chatHistory as-is on failure */ 
      },
    });
  }

  /**
   * Load wells list for a given field name.
   * Called when user selects a field for multi-well analysis.
   * GET /api/v1/wells-list?field_name=X
   */
  loadWellsForField(fieldName: string): void {
    if (!fieldName) return;
    this.selectedField = fieldName;
    this.loadingWells = true;
    this.availableWells = [];
    this.selectedWellIds = [];

    this.wellDataApi.getWellsList(fieldName).subscribe({
      next: (response: any) => {
        this.availableWells = response?.data || [];
        this.loadingWells = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.loadingWells = false;
        this.availableWells = [];
      }
    });
  }

  /**
   * Toggle selection of a well for multi-well dashboard.
   * Called when user checks/unchecks a well in the wells list.
   */
  toggleWellSelection(wellId: string): void {
    const index = this.selectedWellIds.indexOf(wellId);
    if (index > -1) {
      this.selectedWellIds.splice(index, 1);
    } else {
      this.selectedWellIds.push(wellId);
    }
    this.cdr.detectChanges();
  }

  /**
   * Load multi-well dashboard with selected well IDs.
   * Called when user clicks "Load Dashboard" button after selecting wells.
   */
  loadMultiWellDashboard(): void {
    if (this.selectedWellIds.length === 0) {
      return;
    }

    this.isMultiWellMode = true;
    this.fieldWellIds = this.selectedWellIds;
    this.activeNav = 'field';
    this.screen = 'app';
    this.messages = [];  // Clear chat on entry to multi-well dashboard
    this.chatDraft = '';
    
    // Load field data for selected wells
    this.loadFieldData(this.selectedWellIds);
    this.cdr.detectChanges();
  }

  /**
   * Check if a well is selected.
   */
  isWellSelected(wellId: string): boolean {
    return this.selectedWellIds.includes(wellId);
  }

  /** Active session id for the current chat (persists across follow-up messages) */
  currentSessionId: string | null = null;

  // Flag to track if we've already navigated for the first question in this session
  private hasNavigatedForFirstQuestion = false;

  // Flag to track if well_ids have been sent for the first multiwell question
  private multiWellIdsSentForSession = false;

  // Chat panel resize state
  chatPanelWidth: number = 480;
  private _chatResizing = false;
  private _chatResizeStartX = 0;
  private _chatResizeStartWidth = 0;

  /**
   * Listen for window resize events and clamp chat panel width
   * to ensure it doesn't exceed the new viewport constraints
   */
  private setupWindowResizeListener(): void {
    window.addEventListener('resize', () => {
      // Don't apply constraints if user is actively resizing
      if (this._chatResizing) return;
      
      // Clamp chatPanelWidth to the new window constraints
      const maxWidth = window.innerWidth * 0.65;
      if (this.chatPanelWidth > maxWidth) {
        this.chatPanelWidth = Math.max(280, maxWidth);
        this.cdr.detectChanges();
      }
    });
  }

  startChatResize(event: MouseEvent) {
    this._chatResizing = true;
    this._chatResizeStartX = event.clientX;
    this._chatResizeStartWidth = this.chatPanelWidth;
    event.preventDefault();

    const onMouseMove = (e: MouseEvent) => {
      if (!this._chatResizing) return;
      // Dragging left increases width (handle is on the LEFT of chat panel)
      const delta = this._chatResizeStartX - e.clientX;
      const newWidth = Math.max(280, Math.min(window.innerWidth * 0.65, this._chatResizeStartWidth + delta));
      this.chatPanelWidth = Math.round(newWidth);
      this.cdr.detectChanges();
      // Trigger Highcharts reflow so charts resize properly
      window.dispatchEvent(new Event('resize'));
    };

    const onMouseUp = () => {
      this._chatResizing = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      
      // After resize completes, clamp width to current constraints
      const maxWidth = window.innerWidth * 0.65;
      if (this.chatPanelWidth > maxWidth) {
        this.chatPanelWidth = Math.max(280, maxWidth);
        this.cdr.detectChanges();
      }
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  // State
  screen: string = 'landing';
  activeNav: string = 'home';
  selectedSection: string = 'overview';
  selectedSubTab: string = 'wells_summary';
  activeHistoryId: string | null = null;
  chatHistory: any[] = [];
  loading = false;
  loadingHistory = false;
  messages: any[] = [];
  chatLoading = false;
  chatDraft = '';
  chatStatus = '';  // Real-time status from WebSocket (e.g. "Extracting entities…", "Running SQL…")
  pdfGenerating = false;
  loaded = true;
  showAllSuggestedQs = false;
  showTimeLog = false;
  promptPageError: string | null = null;  // Error shown on prompt home page

  // Getter for current user display name from authentication
  get currentUserDisplayName(): string {
    try {
      const appSettings = sessionStorage.getItem('_AppSettings');
      if (appSettings) {
        const settings = JSON.parse(appSettings);
        if (settings && settings.user) {
          const user = settings.user;
          if (user.firstName && user.lastName) {
            return `${user.firstName} ${user.lastName}`;
          } else if (user.name) {
            return user.name;
          }
        }
      }
    } catch (err) {
      throw err
    }
    return 'Well Engineer';
  }
  mobileSidebarOpen = false;
  mobileChatOpen = false;
  selectedWellForTimeLog: string | null = null; // For multi-well time log selection
  hoverWell: string | null = null; // For hover effect on well list
  hasMetricsAccess = signal(false); // Check if user has access to metrics dashboard
  hasRcaAccess = signal(false); // Check if user has access to RCA table validation

  // Feedback survey state
  showFeedbackModal = false;
  feedbackMessageIndex: number | null = null;
  feedbackType: 'like' | 'dislike' | null = null;  // Maps to "sentiment": "Like" or "Dislike"
  feedbackIssueType: 'Data' | 'Graph' | 'Response Summary' | '' = '';  // What was the issue/liked about the response?
  feedbackRating: number = 0;  // 1-5 star rating (response_rating in API)
  feedbackComments: string = '';  // additional_comments
  feedbackError: string = '';  // Error message to display if API fails
  feedbackSubmitting = false;  // Flag to disable button while submitting

  openFeedbackModal(index: number, type: 'like' | 'dislike') {
    this.feedbackMessageIndex = index;
    this.feedbackType = type;
    this.feedbackError = '';
    
    // If message already has feedback, load it into the form
    const currentMessage = this.messages[index];
    if (currentMessage?.feedback) {
      this.feedbackIssueType = currentMessage.feedback.issue_type || '';
      this.feedbackRating = currentMessage.feedback.response_rating || 0;
      this.feedbackComments = currentMessage.feedback.additional_comments || '';
    } else {
      this.feedbackIssueType = '';
      this.feedbackRating = 0;
      this.feedbackComments = '';
    }
    
    this.showFeedbackModal = true;
  }

  /**
   * Check if feedback form has required fields filled
   */
  canSubmitFeedback(): boolean {
    return this.feedbackType !== null && (this.feedbackIssueType !== '' || this.feedbackRating > 0);
  }

  /**
   * Check if a message is a proper response (not clarification, error, or issue)
   * Show feedback/download icons for assistant responses with actual content
   */
  isProperResponse(message: any): boolean {
    // Only show for assistant messages
    if (message?.role !== 'assistant') {
      return false;
    }
    
    // Hide if it's an error message
    if (message?.isError) {
      return false;
    }
    
    // Hide if it's a clarification request or placeholder
    if (message?.responseType === 'clarification' || message?.responseType === 'placeholder') {
      return false;
    }

    // Show feedback for assistant messages with content (text-only or with results/visualization)
    if (message?.content && message.content.trim() !== '') {
      return true;
    }
    
    return false;
  }

  setFeedbackRating(star: number) {
    this.feedbackRating = star;
  }

  submitFeedback() {
    const currentMessage = this.feedbackMessageIndex !== null ? this.messages[this.feedbackMessageIndex] : null;
    const messageId = currentMessage?.message_id || '';
    const sentiment: 'Like' | 'Dislike' = this.feedbackType === 'like' ? 'Like' : 'Dislike';
    const sessionId = this.currentSessionId || '';

    

    // Validate that we have a message_id
    if (!messageId || messageId.trim() === '') {
      this.feedbackError = 'Unable to submit feedback: Message ID is missing. Please try again.';
      this.feedbackSubmitting = false;
      return;
    }

    const feedback = {
      session_id: sessionId,
      message_id: messageId,
      sentiment: sentiment,
      issue_type: this.feedbackIssueType,
      response_rating: this.feedbackRating,
      additional_comments: this.feedbackComments,
    };
    
    // Reset error before submitting
    this.feedbackError = '';
    this.feedbackSubmitting = true;
    
    // Submit feedback to backend API
    this.queryApi.submitFeedback(feedback).subscribe({
      next: (response: any) => {
        // Mark the message with the feedback type
        if (this.feedbackMessageIndex !== null && this.messages[this.feedbackMessageIndex]) {
          this.messages[this.feedbackMessageIndex].feedbackType = this.feedbackType;
        }
        // Close modal on success
        this.feedbackSubmitting = false;
        this.showFeedbackModal = false;
      },
      error: (error: any) => {
        this.feedbackSubmitting = false;
        
        // Extract error message from API response
        let errorMsg = 'Failed to submit feedback. Please try again.';
        if (error.error?.message) {
          errorMsg = error.error.message;
        } else if (error.error?.error) {
          errorMsg = error.error.error;
        } else if (error.message) {
          errorMsg = error.message;
        }
        
        // Display error in modal
        this.feedbackError = errorMsg;
        
        // Still mark the message locally even if API fails
        if (this.feedbackMessageIndex !== null && this.messages[this.feedbackMessageIndex]) {
          this.messages[this.feedbackMessageIndex].feedbackType = this.feedbackType;
        }
      }
    });
  }

  cancelFeedback() {
    this.feedbackError = '';
    this.feedbackSubmitting = false;
    this.showFeedbackModal = false;
  }

  /**
   * Submit RCA (Root Cause Analysis) feedback validation status
   * Called when user validates or marks as needs recheck in causal analysis table
   */
  submitRcaFeedback(event: { eventId: string; verification: string; wellId: string }) {
    const payload = {
      WellID: event.wellId,
      EventID: event.eventId,
      Verification: event.verification
    };


    this.queryApi.submitRcaFeedback(payload).subscribe({
      next: (response: any) => {
        // Reset RCA flags to force reload of causal summary with updated verification status
        this.apiRcaComplete = false;
        this.apiRcaLoading = false;
        this.apiRcaData = [];
        // Reload the causal summary to get updated verification status
        if (this.isMultiWellMode) {
          this.loadMultiWellCausalAnalysis();
        } else {
          const wellId = this._activeWellId;
          if (wellId) {
            this.loadCausalSummary(wellId);
          }
        }
      },
      error: (error: any) => {
        // Show error notification to user
        let errorMsg = 'Failed to submit RCA feedback. Please try again.';
        if (error.error?.message) {
          errorMsg = error.error.message;
        } else if (error.error?.error) {
          errorMsg = error.error.error;
        }
      }
    });
  }

  /**
   * Export a single response message to PDF including question, response, graph/table, and visualizations
   */
  async exportResponseToPdf(msgIndex: number) {
    if (msgIndex < 0 || msgIndex >= this.messages.length) {
      return;
    }

    const message = this.messages[msgIndex];
    if (message.role !== 'assistant') {
      return;
    }

    // Find the user message (question) before this response
    let userMessage = '';
    for (let i = msgIndex - 1; i >= 0; i--) {
      if (this.messages[i].role === 'user') {
        userMessage = this.messages[i].content;
        break;
      }
    }

    // Get the response content
    const responseContent = message.content || '';

    try {
      // Create a new PDF document using the imported jsPDF
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Set font and colors
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 12;
      let yPosition = margin;

      // Title
      doc.setFontSize(18);
      doc.setTextColor(11, 45, 113); // Blue color
      doc.text('P&A Insights Report', margin, yPosition);
      yPosition += 10;

      // Header info
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      const timestamp = new Date().toLocaleString();
      doc.text(`Generated: ${timestamp}`, margin, yPosition);
      yPosition += 8;

      // Question section
      doc.setFontSize(12);
      doc.setTextColor(11, 45, 113);
      doc.text('Question', margin, yPosition);
      yPosition += 6;

      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      const questionLines = doc.splitTextToSize(userMessage || 'N/A', pageWidth - (margin * 2));
      doc.text(questionLines, margin, yPosition);
      yPosition += questionLines.length * 4 + 6;

      // Response section
      doc.setFontSize(12);
      doc.setTextColor(11, 45, 113);
      doc.text('Response', margin, yPosition);
      yPosition += 6;

      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      const responseLines = doc.splitTextToSize(responseContent, pageWidth - (margin * 2));
      doc.text(responseLines, margin, yPosition);
      yPosition += responseLines.length * 4 + 10;

      // Add results table if available
      if (message.results && message.results.length > 0) {
        // Check if we need a new page
        if (yPosition > pageHeight - 80) {
          doc.addPage();
          yPosition = margin;
        }

        doc.setFontSize(12);
        doc.setTextColor(11, 45, 113);
        doc.text('Data Results', margin, yPosition);
        yPosition += 8;

        // Create proper table with borders
        const cols = this.resultColumns(message.results);
        
        if (cols.length === 0) {
          doc.setFontSize(9);
          doc.setTextColor(100, 100, 100);
          doc.text('No data columns available', margin, yPosition);
          yPosition += 6;
        } else {
          // Calculate column widths
          const contentWidth = pageWidth - (margin * 2);
          const colWidth = contentWidth / cols.length;

          // Draw table header with borders
          doc.setFillColor(11, 45, 113);
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(8);
          doc.setFont('helvetica', 'bold');
          
          const headerRowHeight = 8;
          let headerX = margin;
          
          cols.forEach((col: string, idx: number) => {
            const x = margin + (idx * colWidth);
            
            // Draw header cell background
            doc.setFillColor(11, 45, 113);
            doc.rect(x, yPosition, colWidth, headerRowHeight, 'F');
            
            // Draw header cell border
            doc.setDrawColor(100, 100, 100);
            doc.setLineWidth(0.5);
            doc.rect(x, yPosition, colWidth, headerRowHeight);
            
            // Draw text - truncate long column names
            const colName = col.substring(0, 20);
            doc.setTextColor(255, 255, 255);
            doc.text(colName, x + 1, yPosition + 5, { maxWidth: colWidth - 2, align: 'left' });
          });
          
          yPosition += headerRowHeight;
          doc.setFont('helvetica', 'normal');

          // Draw data rows with borders
          doc.setTextColor(0, 0, 0);
          doc.setFontSize(7);
          
          // Limit to 40 rows per page for better readability
          const rowsToShow = message.results.length; // export all rows
          
          for (let i = 0; i < rowsToShow; i++) {
            const row = message.results[i];
            
            // Calculate dynamic row height based on content FIRST
            doc.setFontSize(7);
            let maxCellHeight = 6; // minimum row height
            const cellTextLines: string[][] = [];
            cols.forEach((col: string) => {
              const value = this.formatCellValue(row[col]);
              const displayValue = String(value);
              const lines = doc.splitTextToSize(displayValue, colWidth - 2);
              cellTextLines.push(lines);
              const cellHeight = Math.max(6, lines.length * 3.5 + 2);
              if (cellHeight > maxCellHeight) {
                maxCellHeight = cellHeight;
              }
            });
            const rowHeight = maxCellHeight;

            // Check if we need a new page (using calculated row height)
            if (yPosition + rowHeight > pageHeight - 20) {
              // Add continuation indicator on current page
              yPosition += 2;
              doc.setFontSize(7);
              doc.setTextColor(150, 150, 150);
              doc.text('(continued on next page)', margin, yPosition);
              
              // Add new page
              doc.addPage();
              yPosition = margin;
              
              // Repeat header on new page
              doc.setFillColor(11, 45, 113);
              doc.setTextColor(255, 255, 255);
              doc.setFontSize(8);
              doc.setFont('helvetica', 'bold');
              
              cols.forEach((col: string, idx: number) => {
                const x = margin + (idx * colWidth);
                doc.setFillColor(11, 45, 113);
                doc.rect(x, yPosition, colWidth, headerRowHeight, 'F');
                doc.setDrawColor(100, 100, 100);
                doc.setLineWidth(0.5);
                doc.rect(x, yPosition, colWidth, headerRowHeight);
                const colName = col.substring(0, 20);
                doc.setTextColor(255, 255, 255);
                doc.text(colName, x + 1, yPosition + 5, { maxWidth: colWidth - 2, align: 'left' });
              });
              yPosition += headerRowHeight;
              doc.setFont('helvetica', 'normal');
              doc.setTextColor(0, 0, 0);
              doc.setFontSize(7);
            }

            // Alternate row colors for readability
            if (i % 2 === 0) {
              doc.setFillColor(240, 245, 250);
              cols.forEach((col: string, idx: number) => {
                const x = margin + (idx * colWidth);
                doc.rect(x, yPosition, colWidth, rowHeight, 'F');
              });
            }

            // Draw cell borders and text
            cols.forEach((col: string, idx: number) => {
              const x = margin + (idx * colWidth);
              
              // Draw border
              doc.setDrawColor(200, 200, 200);
              doc.setLineWidth(0.3);
              doc.rect(x, yPosition, colWidth, rowHeight);
              
              // Draw text with dynamic wrapping - vertically centered
              doc.setTextColor(0, 0, 0);
              doc.setFontSize(7);
              const lines = cellTextLines[idx];
              const totalLineHeight = lines.length * 3.5;
              const verticalPadding = (rowHeight - totalLineHeight) / 2; // Center text vertically
              lines.forEach((line: string, lineIdx: number) => {
                const yOffset = yPosition + verticalPadding + 3.5 + (lineIdx * 3.5);
                doc.text(line, x + 0.5, yOffset, { maxWidth: colWidth - 1, align: 'left' });
              });
            });

            yPosition += rowHeight;
          }

          // Add row count indicator
          yPosition += 2;
          doc.setFontSize(8);
          doc.setTextColor(100, 100, 100);
          doc.text(`Total: ${message.results.length} row${message.results.length !== 1 ? 's' : ''}`, margin, yPosition);
          yPosition += 6;
        }
      }

      // Add chart image if available
      if (message.chartOptions && message.visualization && message.visualization.viz_type !== 'kpi_cards') {
        yPosition += 5;

        if (yPosition > pageHeight - 100) {
          doc.addPage();
          yPosition = margin;
        }

        doc.setFontSize(12);
        doc.setTextColor(11, 45, 113);
        doc.text('Chart Visualization', margin, yPosition);
        yPosition += 8;

        // Render chart off-screen with Highcharts API to capture SVG
        try {
          const chartWidth = 780;
          const chartHeight = 380;

          // Merge options, forcing fixed dimensions for export
          const exportOptions: Highcharts.Options = {
            ...message.chartOptions,
            chart: {
              ...(message.chartOptions.chart as any),
              width: chartWidth,
              height: chartHeight,
              animation: false,
            },
            title: {
              ...(message.chartOptions.title as any),
              style: { fontSize: '13px', fontWeight: '600' }
            },
            credits: { enabled: false }
          };

          // Create off-screen container
          const offScreenDiv = document.createElement('div');
          offScreenDiv.style.position = 'absolute';
          offScreenDiv.style.left = '-99999px';
          offScreenDiv.style.top = '0';
          offScreenDiv.style.width = `${chartWidth}px`;
          offScreenDiv.style.height = `${chartHeight}px`;
          document.body.appendChild(offScreenDiv);

          let tempChart: Highcharts.Chart | null = null;
          try {
            tempChart = Highcharts.chart(offScreenDiv, exportOptions);
            // getSVG is added to the Chart prototype by the exporting module
            const svgString = (tempChart as any).getSVG({ chart: { width: chartWidth, height: chartHeight } });

            // Convert SVG → Canvas → PNG data URL
            const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
            const svgUrl = URL.createObjectURL(svgBlob);

            await new Promise<void>((resolve) => {
              const img = new Image();
              img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = chartWidth;
                canvas.height = chartHeight;
                const ctx = canvas.getContext('2d');
                if (ctx) {
                  ctx.fillStyle = '#ffffff';
                  ctx.fillRect(0, 0, chartWidth, chartHeight);
                  ctx.drawImage(img, 0, 0, chartWidth, chartHeight);
                  const pngData = canvas.toDataURL('image/png');
                  const pdfW = pageWidth - margin * 2;
                  const pdfH = (pdfW / chartWidth) * chartHeight;
                  if (yPosition + pdfH > pageHeight - 15) {
                    doc.addPage();
                    yPosition = margin;
                  }
                  doc.addImage(pngData, 'PNG', margin, yPosition, pdfW, pdfH);
                  yPosition += pdfH + 6;
                }
                URL.revokeObjectURL(svgUrl);
                resolve();
              };
              img.onerror = () => { URL.revokeObjectURL(svgUrl); resolve(); };
              img.src = svgUrl;
            });
          } finally {
            if (tempChart) { tempChart.destroy(); }
            document.body.removeChild(offScreenDiv);
          }
        } catch (chartErr) {
          doc.setFontSize(9);
          doc.setTextColor(120, 120, 120);
          doc.text(`Chart: ${message.visualization?.viz_type || 'bar'} — ${message.visualization?.title || ''}`, margin, yPosition);
          yPosition += 6;
        }
      }

      // Add visualization info if available
      // Add footer to all pages
      const pageCount = (doc as any).internal.pages.length - 1;
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(
          `© 2026 Chevron Corporation. Wells P&A Insights System - Page ${i} of ${pageCount}`,
          margin,
          pageHeight - 5
        );
      }

      // Generate filename
      const filename = `P&A_Insights_Report_${new Date().getTime()}.pdf`;

      // Save/Download the PDF
      doc.save(filename);
    } catch (error) {
      alert('Failed to export PDF. Please try again.');
    }
  }

  // Multi-well mode
  isMultiWellMode = false;
  multiWellSelectedNames: string[] = [];
  wellNameMap: { [key: string]: string } = {};
  wellsWithoutNptEvents: string[] = [];  // Wells that were filtered out due to no NPT events
  originalSelectedWellsCount = 0;  // Total number of originally selected wells

  // Multi-well field selection state
  selectedField: string = '';  // From API
  availableWells: any[] = [];  // Wells list from GET /api/v1/wells-list?field_name=X
  loadingWells = false;
  selectedWellIds: string[] = [];  // Selected well IDs for multi-well dashboard

  // Active well data (dynamic)
  // activeWellId initialized to null; set by backend API responses only
  private _activeWellId: string | null = null;
  activeWell: any = null;

  // ── API data state (populated when a well is loaded) ─────────────────────
  apiWellSummary: any = null;       // from /api/v1/wells-summary/{well_id} (single well - object)
  multiWellSummaryData: any[] = [];  // from /api/v1/wells-summary for multi-well (array)

  // Daily Operations (NPT tab) state
  showDailyOps = false;
  selectedWellForDailyOps: string | null = null;
  showSingleWellHistory = false;
  apiDailyOpsRows: { wellName: string; reportStartDate: string; summary: string }[] = [];
  apiDailyOpsLoading = false;
  apiDailyOpsError: string | null = null;
  apiPerformance: any = null;       // from /api/v1/performance-summary/{well_id}
  apiNptSummary: any = null;        // from /api/v1/npt-summary/{well_id}
  apiTimeLog: any[] = [];           // from /api/v1/time-log/{well_id}/{job_id}
  apiJobId: string = '';            // JobID from performance-summary, used for time-log
  apiDataLoading = false;
  technicalResult: string = '';     // from /api/v1/wells-summary/{well_id} data.TechnicalResult
  totalPnaDuration: number = 0;     // from /api/v1/npt-summary/{well_id} data.total_PnA_duration (in hours)
  lastQueryResponse: any = null;    // Store last query API response for display and localStorage

  // Pagination for chatbot response table
  resultsPageSize = 10;             // Show 10 rows per page
  resultsCurrentPage = 0;           // Current page index (0-based)

  // Loading flags for individual tab APIs
  apiNptLoading = false;            // NPT Summary (dashboard tab)
  apiPerformanceLoading = false;    // Cost & Performance tab
  apiWellSummaryLoading = false;    // Well Summary tab
  apiTimeLogLoading = false;        // Time Log modal

  // Error messages for individual tabs
  apiNptError: string | null = null;        // NPT Summary tab error
  apiPerformanceError: string | null = null; // Cost & Performance tab error
  apiWellSummaryError: string | null = null; // Well Summary tab error
  apiTimeLogError: string | null = null;    // Time Log error
  apiFieldDataError: string | null = null;  // Multi-well field data error

  // Root Cause Analysis via HTTP API
  apiRcaData: any[] = [];           // from /causal-summary/{well_id}
  apiRcaLoading = false;
  apiRcaComplete = false;

  // Lessons Learnt via HTTP API
  apiLessonsData: any = null;       // from /api/v1/lookbacklessons
  apiLessonsLoading = false;
  apiLessonsError: string | null = null;
  apiLessonsReport: string = '';    // Raw report text from API

  // Data Quality Popover
  showDataQualityPopover = false;

  // PDF Download
  downloadingPdf = false;

  // Exposed static
  SIDEBAR_SECTIONS = SIDEBAR_SECTIONS;
  fieldPromptId: string = '';

  // Field-level data (populated from API calls to multiple wells)
  fieldWellIds: string[] = [];  // Well IDs for field view
  fieldAggregatedNptEvents: any[] = [];  // All NPT events aggregated from field wells
  fieldAggregatedNptData: any[] = [];  // Aggregated by category
  fieldWellNames: string[] = [];  // Display names for field wells
  wellIdToNameMap: Map<string, string> = new Map();  // Map from well ID to well name

  // Field data getter (returns aggregated data from field well IDs)
  get activeFieldWells(): string[] {
    return this.fieldWellNames;
  }
  get activeFieldWellsLabel(): string {
    return this.fieldWellNames.join(', ');
  }
  get filteredNptEvents() {
    return this.fieldAggregatedNptEvents;
  }
  get filteredFieldNptData() {
    return this.fieldAggregatedNptData;
  }
  get maxFilteredFieldNptHours() { return this.filteredFieldNptData.length ? Math.max(...this.filteredFieldNptData.map(d => d.hours)) : 1; }
  get maxFilteredFieldNptCost() { return this.filteredFieldNptData.length ? Math.max(...this.filteredFieldNptData.map(d => d.cost)) : 1; }
  filteredFieldTotalHrs() { return this.fieldAggregatedNptEvents.reduce((s: number, e: any) => s + (e.duration || 0), 0); }
  filteredFieldTotalCost() { return this.fieldAggregatedNptEvents.reduce((s: number, e: any) => s + (e.cost || 0), 0); }

  // Max values for field charts — will be computed from field data
  maxDetailNptHours = 1;
  maxDetailNptCost = 1;
  maxDetail1yrNptHours = 1;
  maxDetail1yrNptCost = 1;
  maxCompany1yrHours = 1;
  maxCompany1yrCost = 1;
  FIELD_NPT_DETAIL_DATA: any[] = [];  // Dynamic field data
  FIELD_NPT_DETAIL_DATA_1YR: any[] = [];
  FIELD_NPT_COMPANY_1YR: any[] = [];
  FIELD_NPT_CATEGORY_SUMMARY_1YR: any[] = [];

  // Dynamic accessors — prefer live API data, fall back to WELL_DB
  get WELLS_SUMMARY_IDENTIFICATION() {
    if (this.apiWellSummary) {
      const d = this.apiWellSummary;
      const countyState = [d.CountyParish, d.StateProvince].filter(v => v).join(' / ') || '—';
      return [
        { label: 'Well Name',        value: d.WellName },
        { label: 'Field',            value: d.FieldName },
        { label: 'Area',             value: d.Area },
        { label: 'Initial Spud Date',value: d.InitialSpudDate || '—' },
        { label: 'Operator',         value: d.CurrentOperator },
        { label: 'County / State',   value: countyState },
        { label: 'Abandonment Start Date',   value: d.StartDate || '—' },
        { label: 'Abandonment End Date',     value: d.EndDate || '—' },
      ];
    }
    return this.activeWell.identification;
  }

  // Header display info (prefer API data)
  get wellHeaderInfo() {
    if (this.apiWellSummary) {
      return {
        name: this.apiWellSummary.WellName,
        location: this.apiWellSummary.FieldName,
        county: this.apiWellSummary.CountyParish
      };
    }
    return {
      name: this.activeWell.name,
      location: this.activeWell.location || '—',
      county: '—'
    };
  }

  get WELLS_SUMMARY_BACKGROUND_FIELDS() {
    if (this.apiWellSummary) {
      const d = this.apiWellSummary;
      // Only show Age if InitialSpudDate exists
      const hasInitialSpudDate = d.InitialSpudDate && 
                                 d.InitialSpudDate !== 'null' && 
                                 d.InitialSpudDate.toString().trim() !== '';
      const ageValue = hasInitialSpudDate ? this.formatAge(d.AgeOfWell) : '—';
      
      return [
        { label: 'Well Class',          value: d.WellClass || '—' },
        { label: 'Well Type',           value: d.WellType || '—' },
        { label: 'Total Depth',         value: this.formatDepth(d.TotalDepth, 'ft') || '—' },
        { label: 'Age',                 value: ageValue },
        { label: 'Governing Authority', value: d.GoverningAuthority || '—' },
      ];
    }
    return this.activeWell.backgroundFields;
  }

  get WELLS_SUMMARY_JOB_IDENTIFICATION() {
    if (this.apiWellSummary) {
      const d = this.apiWellSummary;
      return [
        { label: 'Job Type',            value: d.JobType || '—' },
        { label: 'Complexity',          value: d.Complexity || '—' },
        { label: 'Well-Safe',           value: d.Well_Safe || '—' },
        { label: 'Plugging Material',    value: d.Plugging_Material || '—' },
        { label: 'Equipment Used',       value: d.EquipmentUsed || '—' },
      ];
    }
    return [];
  }

  get WELLS_SUMMARY_BACKGROUND_LISTS() { return this.activeWell.backgroundLists; }

  get COST_DATA() {
    // In multi-well mode, return null - child component should use multi-well bindings instead
    if (this.isMultiWellMode) {
      return null;
    }
    
    // Single-well mode: use apiPerformance if available
    if (this.apiPerformance && !Array.isArray(this.apiPerformance)) {
      const d = this.apiPerformance;
      return {
        afe: d.AFEAmount,
        actual: d.TotalFieldCostEstimate,
        variance: d.TotalCostVariance,
        plannedAFE: d.AFEAmount,
        breakdown: [
          { category: 'AFE Budget', afe: d.AFEAmount, actual: 0 },
          { category: 'Total Field Cost Estimate', afe: 0, actual: d.TotalFieldCostEstimate },
        ],
        phaseWise: (d.Phases || []).map((p: any) => ({
          WellPhase: p.WellPhase,
          WellPhaseSub: p.SubWellPhase,
          ActualStartDate: p.ActualStartDate,
          ActualEndDate: p.ActualEndDate,
          TotalDurationHours: p.ActualDuration,
          durationDeviation: p.DurationDeviation,
          plannedDuration: p.PlannedAFEDuration,
          PlannedAFECost: p.PlannedAFECost,
          ActualPhaseFieldEstimate: p.ActualPhaseFieldEstimate,
          variance: p.CostVariance,
        })),
        nptCostImpact: this.activeWell.costData?.nptCostImpact || {},
      };
    }
    return this.activeWell.costData;
  }

  get NPT_DATA() {
    if (this.apiNptSummary) {
      // Aggregate NptEvents by MajorCategory → chart-compatible array
      const catMap: Record<string, { driver: string; hours: number; cost: number; events: number; description: string }> = {};
      for (const e of (this.apiNptSummary.NptEvents || [])) {
        const cat = e.MajorCategory || 'OTHER';
        if (!catMap[cat]) {
          catMap[cat] = { driver: cat, hours: 0, cost: 0, events: 0, description: e.Description || '' };
        }
        catMap[cat].hours += e.NetProblemDuration || 0;
        catMap[cat].cost  += e.ProblemCost || 0;
        catMap[cat].events++;
      }
      return Object.values(catMap).sort((a, b) => b.hours - a.hours);
    }
    return this.activeWell?.nptData || [];
  }

  get NPT_EVENTS() {
    if (this.apiNptSummary) {
      return (this.apiNptSummary.NptEvents || []).map((e: any) => ({
        driver: e.MajorCategory,
        wellName: e.WellName,
        description: e.Description,
        cost: e.ProblemCost,
        duration: e.NetProblemDuration,
        startDate: e.StartDate,
        endDate: e.EndDate,
        detail: e.Detail,
        serviceContractor: e.ServiceContractors || '',
      }));
    }
    return this.activeWell.nptEvents || [];
  }

  // Read phase-wise data from COST_DATA getter (already maps API or WELL_DB)
  get PHASE_WISE() {
    // In multi-well mode, return empty array - child component uses multiWellPhaseGrouped instead
    if (this.isMultiWellMode) {
      return [];
    }
    
    // Single-well mode: return phase-wise data from COST_DATA
    const costData = this.COST_DATA;
    if (!costData) return [];
    
    return (costData.phaseWise || []).slice().sort((a: any, b: any) => {
      const parse = (s: string) => {
        if (!s) return 0;
        const [d, t] = s.split(' ');
        if (!d) return 0;
        const [day, month, year] = d.split('-').map(Number);
        const [hour, min] = (t || '00:00').split(':').map(Number);
        return new Date(year, month - 1, day, hour, min).getTime();
      };
      return parse(a.ActualStartDate) - parse(b.ActualStartDate);
    });
  }
  get TIMELINE_DATA() { return this.activeWell?.timeline || []; }
  get LESSONS() { return this.activeWell?.lessons || []; }
  get COST_RECOMMENDED_ACTIONS() { return this.activeWell?.recommendedActions || []; }
  get OVERVIEW_PLANNED_DAYS() { return this.activeWell?.plannedDays || 0; }
  get OVERVIEW_ACTUAL_DAYS() { return this.activeWell?.actualDays || 0; }
  get OVERVIEW_NPT_TOTAL_HRS() {
    if (this.apiNptSummary) return this.apiNptSummary.TotalNptHours || 0;
    if (!this.activeWell?.nptData || !Array.isArray(this.activeWell.nptData)) return 0;
    return this.activeWell.nptData.reduce((s: number, n: any) => s + n.hours, 0);
  }
  get OVERVIEW_NPT_PCT() {
    // For API-driven wells, calculate percentage from total NPT hours and total P&A duration
    if (this.activeWell?.apiDriven && this.totalPnaDuration > 0) {
      const nptHours = this.OVERVIEW_NPT_TOTAL_HRS;
      return ((nptHours / this.totalPnaDuration) * 100).toFixed(2);
    }
    // Fallback for non-API wells
    return this.activeWell?.nptPct || 0;
  }
  get OVERVIEW_AI_SUMMARY() { return this.activeWell.aiSummary; }
  get selectedWell() { return { name: this.activeWell.name, location: this.activeWell.location, block: this.activeWell.block }; }
  get CHATBOT_SUGGESTED_QS() {
    // In multi-well mode, return multi-well specific questions
    if (this.isMultiWellMode) {
      return [
        'What is the total P&A duration across all wells?',
        'Which wells had the most NPT?',
        'What is the total cost variance?',
        'Which phase had the most cost overrun?'
      ];
    }
    // In single-well mode, return well-specific questions
    return this.activeWell.followUpQA.map((qa: any) => qa.q);
  }
  get ACTIVE_WELL_CAUSAL_ANALYSIS() {
    // Prefer RCA API data over NPT summary API
    if (this.apiRcaData.length) {
      return this.apiRcaData;
    }
    if (this.apiNptSummary?.Causes?.length) {
      return this.apiNptSummary.Causes.map((c: any) => ({
        wellName: this.apiNptSummary.WellName || this.activeWell.name,
        nptEvent: c.Description,
        rootCause: (c.RootCause || '').split('\n').map((line: string) => line.trim()).join('\n'),
      }));
    }
    // No fallback to hardcoded data; use API only
    return [];
  }
  get ACTIVE_WELL_NAME(): string {
    return this.activeWell?.name
      || this.apiWellSummary?.WellName
      || (!Array.isArray(this.apiNptSummary) ? this.apiNptSummary?.WellName : '')
      || (this._activeWellId ? this.wellIdToNameMap.get(this._activeWellId) : '')
      || (this._activeWellId ? this.wellNameMap[this._activeWellId] : '')
      || this._activeWellId
      || '';
  }
  get ACTIVE_WELL_TIME_LOG() {
    if (this.apiTimeLog.length) {
      return [...this.apiTimeLog].sort((a: any, b: any) => {
        return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
      });
    }
    return [];  // No hardcoded fallback; use API data only
  }
  get paDurationDays() {
    const durationHrs = this.isMultiWellMode ? this.multiWellTotalHrs : this.totalPnaDuration;
    return (durationHrs / 24).toFixed(1);
  }

  /** Returns true if technicalResult means "met all objectives" (case-insensitive) */
  isTechnicalResultMet(): boolean {
    const val = (this.technicalResult || this.activeWell?.technicalResult || '').toLowerCase();
    return val.includes('met all');
  }

  /** Capitalizes the first letter of a string */
  capitalizeFirst(str: string): string {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  // Multi-well computed data
  get multiWellCards() {
    // Use API data if available, otherwise fallback to mock catalog
    if (this.isMultiWellMode && this.multiWellSummaryData && Array.isArray(this.multiWellSummaryData)) {
      return this.multiWellSummaryData.map((well: any) => ({
        name: well.WellName || well.WellID || '',
        field: well.FieldName || '—',
        area: well.Area || '—',
        jobType: well.JobType || '—',
        abandonmentStart: well.StartDate || '—',
        abandonmentEnd: well.EndDate || '—',
        wellId: well.WellID || '',
      }));
    }
    // No fallback — use API data only
    return [];
  }
  get multiWellNptEvents() {
    // Use API data if available
    if (this.isMultiWellMode && this.fieldAggregatedNptEvents && this.fieldAggregatedNptEvents.length > 0) {
      return this.fieldAggregatedNptEvents;
    }
    // No fallback — use API data only
    return [];
  }
  get multiWellCausalAnalysis() {
    // Use API data if available
    if (this.isMultiWellMode && this.apiRcaData && this.apiRcaData.length > 0) {
      return this.apiRcaData;
    }
    // No fallback — use API data only
    return [];
  }
  get multiWellPhasewise() {
    // Use API data if available
    if (this.isMultiWellMode && this.apiPerformance && Array.isArray(this.apiPerformance)) {
      // Flatten phases from all wells
      return this.apiPerformance.flatMap((well: any) => {
        const phases = well.Phases || [];
        return phases.map((phase: any) => ({
          ...phase,
          wellName: well.WellName || well.WellID || '',
          wellId: well.WellID || ''
        }));
      });
    }
    // No fallback — use API data only
    return [];
  }

 

    get multiWellPhaseGrouped() {
    // Use API data if available - return INDIVIDUAL phases, not aggregated
    if (this.isMultiWellMode && this.apiPerformance && Array.isArray(this.apiPerformance)) {
      const result: any[] = [];
      
      this.apiPerformance.forEach((well: any) => {
        const phases = well.Phases || [];
        phases.forEach((phase: any) => {
          result.push({
            wellName: well.WellName || well.WellID || '',
            primaryJobType: phase.WellPhase || '—',
            subPhase: phase.SubWellPhase || '—',
            plannedDuration: parseFloat((phase.PlannedAFEDuration || 0).toFixed(2)),
            totalDuration: parseFloat((phase.ActualDuration || 0).toFixed(2)),
            durationDeviation: parseFloat((phase.DurationDeviation || 0).toFixed(2)),
            totalPlannedCost: parseFloat((phase.PlannedAFECost || 0).toFixed(2)),
            totalActualCost: parseFloat((phase.ActualPhaseFieldEstimate || 0).toFixed(2)),
            totalVariance: parseFloat((phase.CostVariance || 0).toFixed(2))
          });
        });
      });
      
      return result;
    }
    
    // Fallback to mock data - also return individual phases
    const result: any[] = [];
    this.multiWellCards.forEach((well: any) => {
      const phases = well.phasewise || [];
      phases.forEach((phase: any) => {
        result.push({
          wellName: well.name,
          primaryJobType: phase.WellPhase || '—',
          subPhase: phase.WellPhaseSub || '—',
          plannedDuration: parseFloat((phase.plannedDuration || 0).toFixed(2)),
          totalDuration: parseFloat((phase.TotalDurationHours || 0).toFixed(2)),
          durationDeviation: parseFloat((phase.durationDeviation || 0).toFixed(2)),
          totalPlannedCost: parseFloat((phase.PlannedAFECost || 0).toFixed(2)),
          totalActualCost: parseFloat((phase.ActualPhaseFieldEstimate || 0).toFixed(2)),
          totalVariance: parseFloat((phase.variance || 0).toFixed(2))
        });
      });
    });
    
    return result;
  }

  get multiWellTotalNptHrs() {
    if (this.isMultiWellMode && this.apiNptSummary && Array.isArray(this.apiNptSummary)) {
      return this.apiNptSummary.reduce((s: number, well: any) => s + (well.TotalNptHours || 0), 0);
    }
    return this.multiWellCards.reduce((s: number, w: any) => s + (w?.nptHrs || 0), 0);
  }
  get multiWellTotalNptCost() {
    if (this.isMultiWellMode) {
      // Prefer direct API totals from NPT summary (most reliable for cost tab aggregation)
      if (Array.isArray(this.apiNptSummary) && this.apiNptSummary.length > 0) {
        return this.apiNptSummary.reduce((s: number, well: any) => s + (well?.TotalNptCost || 0), 0);
      }

      // Fallback: aggregate from category breakdown
      if (Array.isArray(this.fieldAggregatedNptData) && this.fieldAggregatedNptData.length > 0) {
        return this.fieldAggregatedNptData.reduce((s: number, cat: any) => s + (cat?.cost || 0), 0);
      }

      // Fallback: performance API totals (if available)
      if (Array.isArray(this.apiPerformance) && this.apiPerformance.length > 0) {
        return this.apiPerformance.reduce((s: number, well: any) => s + (well?.TotalNptCost || 0), 0);
      }

      // Last fallback: sum event-level costs
      return this.multiWellNptEvents.reduce((s: number, e: any) => s + (e?.cost || 0), 0);
    }

    return 0;
  }
  get multiWellTotalHrs() {
    if (this.isMultiWellMode && this.apiNptSummary && Array.isArray(this.apiNptSummary)) {
      return this.apiNptSummary.reduce((s: number, well: any) => s + (well.total_PnA_duration || 0), 0);
    }
    return this.multiWellCards.reduce((s: number, w: any) => s + (w?.totalHrs || 0), 0);
  }
  get multiWellTotalPlannedAFE() {
    if (this.isMultiWellMode && this.apiPerformance && Array.isArray(this.apiPerformance)) {
      const result = this.apiPerformance.reduce((s: number, well: any) => s + (well.AFEAmount || 0), 0);
      return result;
    }
    const fallback = this.multiWellCards.reduce((s: number, w: any) => s + (w?.afe || 0), 0);
    return fallback;
  }
  get multiWellTotalActualCost() {
    if (this.isMultiWellMode && this.apiPerformance && Array.isArray(this.apiPerformance)) {
      const result = this.apiPerformance.reduce((s: number, well: any) => s + (well.TotalFieldCostEstimate || 0), 0);
      return result;
    }
    const fallback = this.multiWellCards.reduce((s: number, w: any) => s + (w?.actual || 0), 0);
    return fallback;
  }
  get multiWellTotalVariance() {
    if (this.isMultiWellMode && this.apiPerformance && Array.isArray(this.apiPerformance)) {
      const result = this.apiPerformance.reduce((s: number, well: any) => s + (well.TotalCostVariance || 0), 0);
      return result;
    }
    const fallback = this.multiWellCards.reduce((s: number, w: any) => s + (w?.variance || 0), 0);
    return fallback;
  }

  /** Per-well cost summary for multi-well cost & performance table */
  get multiWellCostSummary() {
    if (this.isMultiWellMode && this.apiPerformance && Array.isArray(this.apiPerformance)) {
      return this.apiPerformance.map((well: any) => {
        const planned = Number(well.AFEAmount || 0);
        const actual = Number(well.TotalFieldCostEstimate || 0);
        const variance = Number(well.TotalCostVariance || 0);
        const variancePct = planned !== 0 ? (variance / planned) * 100 : 0;
        return {
          wellName: well.WellName || well.WellID || '—',
          wellId: well.WellID || '',
          plannedAFE: planned,
          actualCost: actual,
          variance: variance,
          variancePct: variancePct,
          plannedDuration: Number(well.TotalPlannedDuration || 0),
          actualDuration: Number(well.TotalActualDuration || 0),
          durationVariance: Number(well.TotalDurationVariance || 0)
        };
      });
    }
    // Fallback to mock data
    return this.multiWellCards.map((w: any) => {
      const planned = Number(w?.afe || 0);
      const actual = Number(w?.actual || 0);
      const variance = Number(w?.variance || 0);
      const variancePct = planned !== 0 ? (variance / planned) * 100 : 0;
      return {
        wellName: w?.name || '—',
        wellId: w?.wellId || '',
        plannedAFE: planned,
        actualCost: actual,
        variance: variance,
        variancePct: variancePct,
        plannedDuration: Number(w?.plannedDuration || 0),
        actualDuration: Number(w?.totalDuration || 0),
        durationVariance: Number(w?.durationDeviation || 0)
      };
    });
  }
  get multiWellLessons() {
    // Use API data if available
    if (this.isMultiWellMode && this.apiRcaData && this.apiRcaData.length > 0) {
      return this.apiRcaData.map((lesson: any) => ({
        wellName: lesson.WellName || '—',
        nptEvent: lesson.NptEvent || '—',
        rootCause: lesson.RootCause || '—'
      }));
    }
    // No fallback — use API data only
    return [];
  }
  get multiWellLessonsByWell() {
    // Use API data if available
    if (this.isMultiWellMode && this.apiRcaData && this.apiRcaData.length > 0) {
      const wellMap = new Map<string, any[]>();
      this.apiRcaData.forEach((lesson: any) => {
        const wellName = lesson.WellName || '—';
        if (!wellMap.has(wellName)) {
          wellMap.set(wellName, []);
        }
        wellMap.get(wellName)!.push({
          category: lesson.Category || '—',
          insight: lesson.Insight || '—',
          priority: lesson.Priority || '—'
        });
      });
      return Array.from(wellMap.entries()).map(([wellName, lessons]) => ({
        wellName,
        lessons
      }));
    }
    // No fallback — use API data only
    return [];
  }

  private loadWell(wellId: string, showLoader = false) {
    // No WELL_DB fallback; wellId must come from API response only
    if (!wellId) {
      this.loading = false;
      return;
    }
    this._activeWellId = wellId;
    
    // Initialize basic activeWell structure from wellId (no hardcoded data)
    this.activeWell = { name: '', location: '', id: wellId, apiDriven: true };
    this.showAllSuggestedQs = false;

    // Clear cached API data
    this.apiWellSummary = null;
    this.apiPerformance = null;
    this.apiNptSummary = null;
    this.apiTimeLog = [];
    this.apiJobId = '';
    this.apiRcaData = [];
    this.apiRcaLoading = false;
    this.apiRcaComplete = false;

    if (showLoader) this.loading = true;

    // Load API data for the current tab (always API-driven since WELL_DB is removed)
    this.loadApiForTab(this.selectedSubTab, wellId, showLoader);
  }

  /** Called on well load AND whenever the user switches sidebar tabs */
  loadApiForTab(subTab: string, wellId?: string, showLoader = false) {
    // Handle multi-well mode
    if (this.isMultiWellMode) {
      if (subTab === 'wells_summary') {
        // Summary is already loaded in loadFieldData
        if (showLoader) this.loading = false;
        return;
      }
      if (subTab === 'dashboard') {
        this.loadMultiWellNptData();
        this.loadMultiWellCausalAnalysis(); // Load causal analysis for multi-well
        if (showLoader) this.loading = false;
        return;
      }
      if (subTab === 'cost_performance') {
        this.loadMultiWellNptData();
        this.loadMultiWellPerformanceData();
        if (showLoader) this.loading = false;
        return;
      }
      if (subTab === 'lessons') {
        this.loadLessonsLearned(this.fieldWellIds);
        if (showLoader) this.loading = false;
        return;
      }
      return;
    }

    // Single well mode (original logic)
    const id = wellId || this._activeWellId;
    if (!id) {
      if (showLoader) this.loading = false;
      return;
    }

    let apisToLoad = 0;
    let apisLoaded = 0;
    const checkAllLoaded = () => {
      apisLoaded++;
      if (showLoader && apisLoaded >= apisToLoad) this.loading = false;
    };

    // Prevent duplicate API calls for well summary
    if (subTab === 'wells_summary' && !this.apiWellSummaryLoading && !this.apiWellSummary) {
      apisToLoad++;
      this.apiWellSummaryLoading = true;
      this.apiWellSummaryError = null;
      this.wellDataApi.getWellsSummary(id).subscribe({
        next: (res) => {
          // API returns array, extract first element for single well
          const summaryData = Array.isArray(res.data) ? res.data[0] : res.data;
          this.apiWellSummary = summaryData;
          this.technicalResult = summaryData?.TechnicalResult || '';
          this.apiWellSummaryError = null;
          this.apiWellSummaryLoading = false;
          checkAllLoaded();
        },
        error: (err) => {
          this.apiWellSummaryError = 'Failed to load well summary. Please try again.';
          this.apiWellSummaryLoading = false;
          checkAllLoaded();
        },
      });
    }

    if (subTab === 'cost_performance' && !this.apiPerformance && !this.apiPerformanceLoading) {
      apisToLoad++;
      this.apiPerformanceLoading = true;
      this.apiPerformanceError = null;
      this.wellDataApi.getPerformanceSummary(id).subscribe({
        next: (res) => {
          // In multi-well mode, keep the full array; for single well, extract first element
          const perfData = this.isMultiWellMode ? res.data : (Array.isArray(res.data) ? res.data[0] : res.data);
          this.apiPerformance = perfData;
          this.apiJobId = !this.isMultiWellMode ? (perfData?.JobID || '') : '';
          this.apiPerformanceError = null;
          this.apiPerformanceLoading = false;
          checkAllLoaded();
        },
        error: (err) => {
          this.apiPerformanceError = 'Failed to load performance data. Please try again.';
          this.apiPerformanceLoading = false;
          checkAllLoaded();
        },
      });
    }

    if (subTab === 'dashboard' && !this.apiNptSummary && !this.apiNptLoading) {
      apisToLoad++;
      this.apiNptLoading = true;
      this.apiNptError = null;
      this.wellDataApi.getNptSummary(id).subscribe({
        next: (res) => {
          // API returns array, extract first element for single well
          const nptData = Array.isArray(res.data) ? res.data[0] : res.data;
          this.apiNptSummary = nptData;
          this.totalPnaDuration = nptData?.total_PnA_duration || 0;
          this.apiNptError = null;
          this.apiNptLoading = false;
          checkAllLoaded();
        },
        error: (err) => {
          this.apiNptError = 'Failed to load NPT summary. Please try again.';
          this.apiNptLoading = false;
          checkAllLoaded();
        },
      });
    }

    // Causal Summary should be called only when user is on NPT Summary tab (dashboard)
    if (subTab === 'dashboard' && !this.apiRcaComplete && !this.apiRcaLoading) {
      this.loadCausalSummary(id);
    }

    // Load Lessons Learnt when user navigates to lessons tab (single well mode)
    if (subTab === 'lessons' && !this.apiLessonsLoading && !this.apiLessonsData) {
      this.loadLessonsLearned(id);
    }

    // If nothing to load, clear loader
    if (showLoader && apisToLoad === 0) this.loading = false;
  }

  /** Call this to open the time-log modal — fetches lazily. For multi-well, pass selectedWellId */
  populateMultiWellSelectedNames() {
    // Multi-well mode: populate well list from NPT summary data (only wells with NPT events)
    if (this.isMultiWellMode && this.fieldWellIds.length > 0) {
      // Store the original count of selected wells
      this.originalSelectedWellsCount = this.fieldWellIds.length;
      
      this.wellNameMap = {};
      this.wellsWithoutNptEvents = [];
      
      
      
      if (this.apiNptSummary && this.apiNptSummary.length > 0) {
        // Only include wells that have NPT events in the summary
        this.multiWellSelectedNames = [];
        this.apiNptSummary.forEach((well: any) => {
          const wellId = well.WellID;
          const wellName = well.WellName || wellId;
          const hasNptEvents = well.NptEvents && Array.isArray(well.NptEvents) && well.NptEvents.length > 0;
          
          
          // Only add to the list if there are NPT events for this well
          if (hasNptEvents) {
            this.multiWellSelectedNames.push(wellId);
            this.wellNameMap[wellId] = wellName;
          } else {
            // Track wells without NPT events
            this.wellsWithoutNptEvents.push(wellId);
          }
        });
      } else {
        // Fallback: use all field well IDs
        this.multiWellSelectedNames = [...this.fieldWellIds];
        this.fieldWellIds.forEach(wellId => {
          this.wellNameMap[wellId] = wellId;
        });
      }
      
      
      
      this.cdr.markForCheck();
    }
  }

  /**
   * Well History rows (single well) from wells-summary PriorJobs
   */
  get WELL_HISTORY_SINGLE(): { jobStartDate: string; primaryJobType: string }[] {
    const jobs = this.apiWellSummary?.PriorJobs ?? this.apiWellSummary?.priorJobs;
    if (!Array.isArray(jobs)) return [];
    return jobs.map((j: any) => ({
      jobStartDate: this.formatJobDate(this.pickHistoryField(j, ['JobStartDate', 'jobStartDate', 'Job_Start_Date', 'StartDate', 'Start_Date'])) || 'Date not provided',
      primaryJobType: this.pickHistoryField(j, ['PrimaryJobType', 'primaryJobType', 'Primary_Job_Type', 'JobType']) || 'Type not provided',
    }));
  }

  /** Returns the first non-empty value among candidate keys on a history row */
  private pickHistoryField(row: any, keys: string[]): string {
    if (!row) return '';
    for (const k of keys) {
      const v = row[k];
      if (v !== undefined && v !== null && String(v).trim() !== '') return String(v);
    }
    return '';
  }

  /** Formats a job date as "2024-02-09" (YYYY-MM-DD); returns the raw value if it cannot be parsed */
  formatJobDate(value: any): string {
    if (!value) return '';
    const raw = String(value);
    // Keep the calendar date as sent by the API to avoid timezone shifts
    const isoMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoMatch) return isoMatch[1];
    const d = new Date(raw);
    if (isNaN(d.getTime())) return raw;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  /**
   * Well History rows (multi-well) from wells-summary PriorJobs, flattened per well
   */
  get WELL_HISTORY_MULTI(): { wellName: string; jobStartDate: string; primaryJobType: string }[] {
    if (!this.isMultiWellMode || !Array.isArray(this.multiWellSummaryData)) return [];
    return this.multiWellSummaryData.flatMap((w: any) =>
      (Array.isArray(w?.PriorJobs) ? w.PriorJobs : []).map((j: any) => ({
        wellName: w?.WellName || w?.WellID || '',
        jobStartDate: j?.JobStartDate || '—',
        primaryJobType: j?.PrimaryJobType || '—',
      }))
    );
  }

  /** Toggle the Daily Operations modal and load data when opening */
  toggleDailyOps() {
    this.showDailyOps = !this.showDailyOps;
    this.selectedWellForDailyOps = null;
    this.apiDailyOpsRows = [];
    this.apiDailyOpsError = null;
    // Multi-well: user picks a well first; single-well: load immediately
    if (this.showDailyOps && !this.isMultiWellMode) {
      this.loadDailyOperations();
    }
  }

  closeDailyOps() {
    this.showDailyOps = false;
    this.selectedWellForDailyOps = null;
    this.apiDailyOpsRows = [];
    this.apiDailyOpsError = null;
  }

  /** Wells eligible for Daily Operations: NPT summary rows that have both WellID and JobID */
  get DAILY_OPS_WELLS(): { id: string; name: string }[] {
    const npt = Array.isArray(this.apiNptSummary) ? this.apiNptSummary : [];
    return npt
      .filter((w: any) => w?.WellID && w?.JobID)
      .map((w: any) => ({
        id: w.WellID,
        name: w.WellName || this.wellIdToNameMap.get(w.WellID) || w.WellID,
      }));
  }

  /** Multi-well: select a well, then load its daily report only */
  selectWellForDailyOps(wellId: string) {
    this.selectedWellForDailyOps = wellId;
    this.loadDailyOperations(wellId);
  }

  /**
   * POST /api/v1/daily-report for the active well (single), or for the chosen well (multi)
   */
  loadDailyOperations(wellId?: string) {
    if (this.apiDailyOpsLoading) return;

    let wellIds: string[] = [];
    let jobIds: string[] = [];

    if (this.isMultiWellMode) {
      const npt = Array.isArray(this.apiNptSummary) ? this.apiNptSummary : [];
      const pair = npt.find((w: any) => w?.WellID === wellId && w?.JobID);
      if (pair) {
        wellIds = [pair.WellID];
        jobIds = [pair.JobID];
      }
    } else {
      const id = this._activeWellId;
      const jobId = this.apiNptSummary?.JobID;
      if (id && jobId) {
        wellIds = [id];
        jobIds = [jobId];
      }
    }

    if (wellIds.length === 0 || jobIds.length === 0) {
      this.apiDailyOpsError = 'Well or JobID not found. Please ensure NPT data is loaded.';
      this.cdr.markForCheck();
      return;
    }

    this.apiDailyOpsLoading = true;
    this.apiDailyOpsError = null;
    this.apiDailyOpsRows = [];

    this.wellDataApi.getDailyReport(wellIds, jobIds).subscribe({
      next: (res: any) => {
        const data = Array.isArray(res?.data) ? res.data : [];
        this.apiDailyOpsRows = data.flatMap((w: any) =>
          (Array.isArray(w?.dailyReportEntries) ? w.dailyReportEntries : []).map((e: any) => ({
            wellName: w?.WellName || w?.WellID || '',
            reportStartDate: e?.ReportStartDate || '—',
            summary: e?.Summary || '—',
          }))
        );
        this.apiDailyOpsLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.apiDailyOpsError = 'Failed to load daily operations. Please try again.';
        this.apiDailyOpsLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  loadTimeLog(selectedWellId?: string) {
    // Multi-well mode
    if (this.isMultiWellMode) {
      this.populateMultiWellSelectedNames();
      this.loadMultiWellTimeLog(selectedWellId);
      return;
    }

    // Single well mode (original logic)
    const id = this._activeWellId;
    if (!id) return; // Do nothing if no active well
    if (this.apiTimeLog.length || this.apiTimeLogLoading) return;

    // Show loading spinner immediately when modal opens
    this.apiTimeLogLoading = true;
    this.apiTimeLogError = null;

    // Need jobId — extract from NPT summary (already loaded) or return error
    const doFetch = (jobId: string) => {
      if (!jobId || !id) {
        this.apiTimeLogLoading = false;
        this.apiTimeLogError = 'JobID not found. Please ensure NPT data is loaded.';
        this.cdr.markForCheck();
        return;
      }
      this.wellDataApi.getTimeLog(id, jobId).subscribe({
        next: (res) => {
          // API returns array, extract first element and get timeLogEntries
          const timeLogData = Array.isArray(res.data) ? res.data[0] : res.data;
          this.apiTimeLog = (timeLogData?.timeLogEntries || []).map((e: any) => ({
            activityCode: e.activityCode,
            comment:      e.comment,
            startDate:    e.startDate,
            endDate:      e.endDate,
          }));
          this.apiTimeLogError = null;
          this.apiTimeLogLoading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.apiTimeLogError = 'Failed to load time log data. Please try again.';
          this.apiTimeLogLoading = false;
          this.cdr.markForCheck();
        },
      });
    };

    // Extract JobID from NPT summary (already available)
    const jobIdFromNpt = this.apiNptSummary?.JobID;
    if (jobIdFromNpt) {
      doFetch(jobIdFromNpt);
    } else {
      this.apiTimeLogLoading = false;
      this.apiTimeLogError = 'NPT summary data not loaded. Please refresh and try again.';
      this.cdr.markForCheck();
    }
  }

  /**
   * Load Lessons Learnt via the Lookback Lessons API
   * Supports both single well and multi-well modes
   */
  loadLessonsLearned(wellIds: string | string[]) {
    if (this.apiLessonsLoading) return;

    // Normalize wellIds to array
    const ids = Array.isArray(wellIds) ? wellIds : [wellIds];
    if (ids.length === 0) return;

    this.apiLessonsLoading = true;
    this.apiLessonsError = null;
    this.apiLessonsReport = '';
    this.apiLessonsData = null;


    this.httpService.getLessonsLearned(ids).subscribe({
      next: (response: any) => {
        this.apiLessonsData = response;
        // Combine all reports from the reports array
        if (response.reports && Array.isArray(response.reports)) {
          this.apiLessonsReport = response.reports
            .map((r: any) => r.report)
            .filter((r: any) => r)
            .join('\n\n---\n\n');
        } else {
          this.apiLessonsReport = '';
        }
        this.apiLessonsError = null;
        this.apiLessonsLoading = false;
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        this.apiLessonsError = 'Failed to load Lessons Learned. Please try again.';
        this.apiLessonsLoading = false;
        this.apiLessonsData = null;
        this.apiLessonsReport = '';
        this.cdr.detectChanges();
      }
    });
  }

  /** Load Root Cause Analysis via HTTP at /causal-summary/{well_id} */
  loadCausalSummary(wellId: string) {
    if (this.apiRcaComplete || this.apiRcaLoading) return;

    this.apiRcaLoading = true;
    this.apiRcaData = [];
    this.apiRcaComplete = false;

    this.wellDataApi.getCausalSummary(wellId).subscribe({
      next: (res: any) => {
        // API returns array of wells with causes data
        const data = Array.isArray(res?.data) ? res.data[0] : res?.data || {};
        const causes = Array.isArray(data?.causes) ? data.causes : (Array.isArray(data?.CausalAnalysis) ? data.CausalAnalysis : []);

        this.apiRcaData = causes.map((c: any) => ({
          wellName: data.WellName || this.activeWell?.name || '',
          nptEvent: c.Description || '',
          rootCause: (c.RootCause || '').split('\n').map((l: string) => l.trim()).join('\n'),
          confidence: c.ConfidenceScore || 0,
          evidence: c.Evidence || '',
          eventId: c.EventID || '',
          jobId: c.JobID || '',
          VerificationStatus: c.VerificationStatus || null,
          VerifiedBy: c.VerifiedBy || null,
        }));

        this.apiRcaComplete = true;
        this.apiRcaLoading = false;
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        this.apiRcaLoading = false;
        this.apiRcaComplete = false;
        this.cdr.detectChanges();
      }
    });
  }

  // Multi-well time log methods
  selectWellForTimeLog(wellId: string) {
    // Toggle selection
    if (this.selectedWellForTimeLog === wellId) {
      this.selectedWellForTimeLog = null;
      this.apiTimeLog = [];
      this.apiTimeLogError = null;
      this.apiTimeLogLoading = false;
      this.cdr.markForCheck();
      return;
    }
    
    // Clear previous data when switching wells
    this.apiTimeLog = [];
    this.apiTimeLogError = null;
    this.apiTimeLogLoading = false;
    
    this.selectedWellForTimeLog = wellId;
    
    // For multi-well, we need to fetch time log for the selected well
    if (this.isMultiWellMode) {
      this.loadTimeLogForSelectedWell(wellId);
    }
  }

  /**
   * Load time log for a specific well in multi-well mode
   */
  private loadTimeLogForSelectedWell(wellId: string) {
    if (!wellId) return;
    
    this.apiTimeLogLoading = true;
    this.apiTimeLogError = null;
    
    // Get JobID from NPT summary (already loaded)
    const wellNptData = this.apiNptSummary?.find((well: any) => well.WellID === wellId);
    const jobId = wellNptData?.JobID;
    
    if (!jobId) {
      this.apiTimeLogError = 'No job ID found for this well. Please ensure NPT data is loaded.';
      this.apiTimeLogLoading = false;
      this.cdr.markForCheck();
      return;
    }
    
    // Fetch time log for this specific well
    this.wellDataApi.getTimeLog(wellId, jobId).subscribe({
      next: (res) => {
        // Filter response to only include data for the selected well
        let timeLogData = res.data;
        if (Array.isArray(timeLogData)) {
          timeLogData = timeLogData.find((item: any) => item.WellID === wellId);
        }
        
        this.apiTimeLog = (timeLogData?.timeLogEntries || []).map((e: any) => ({
          activityCode: e.activityCode,
          comment:      e.comment,
          startDate:    e.startDate,
          endDate:      e.endDate,
        }));
        this.apiTimeLogError = null;
        this.apiTimeLogLoading = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.apiTimeLogError = 'Failed to load time log. Please try again.';
        this.apiTimeLog = [];
        this.apiTimeLogLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Get time log entries for a specific well
   * Used in template to display time log data
   */
  getTimeLogForWell(wellId: string | null): any[] {
    if (!wellId || wellId !== this.selectedWellForTimeLog) {
      return [];
    }
    return this.apiTimeLog || [];
  }

  get showNavSidebar() { return this.activeNav !== 'home' && this.activeNav !== 'field'; }
  get promptHome() { return this.activeNav === 'home'; }
  get fieldView() { return this.activeNav === 'field'; }

  /**
   * Get the active nav item ID for sidebar highlighting.
   * Maps selectedSubTab back to the item ID.
   */
  get activeNavItemIdForSidebar(): string | null {
    // Map selectedSubTab back to item IDs
    const tabToIdMap: Record<string, string> = {
      'wells_summary': 'wells_summary',
      'dashboard': 'dash',
      'cost_performance': 'cost_performance',
      'lessons': 'lessons',
    };
    return tabToIdMap[this.selectedSubTab] || null;
  }

  fmt(n: number) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
  }
  fmtM(n: number) { return '$' + (n / 1000000).toFixed(2) + 'M'; }
  fmtK(n: number) { 
    const isNegative = n < 0;
    const formatted = '$' + Math.abs(n / 1000).toFixed(2).replace(/\.00$/, '') + 'K';
    return isNegative ? '-' + formatted : formatted;
  }

  /** Format depth with unit (ft or m) */
  formatDepth(depth: number | undefined, unit: string = 'ft'): string {
    if (!depth || depth === 0) return '—';
    return `${depth.toLocaleString()} ${unit}`;
  }

  /** Convert days to Years, Months, and Days format */
  formatAge(days: number | string | undefined): string {
    if (!days) return '—';
    const numDays = typeof days === 'string' ? parseFloat(days) : days;
    if (isNaN(numDays) || numDays <= 0) return '—';
    
    const years = Math.floor(numDays / 365);
    const remainingDays1 = numDays % 365;
    const months = Math.floor(remainingDays1 / 30);
    const remainingDays2 = Math.floor(remainingDays1 % 30);
    
    const parts: string[] = [];
    if (years > 0) parts.push(`${years}yr`);
    if (months > 0) parts.push(`${months}mon`);
    if (remainingDays2 > 0) parts.push(`${remainingDays2}d`);
    
    return parts.length > 0 ? parts.join(' ') : '—';
  }

  rcaStatusMap: Record<number, string> = {};
  rcaSubmittedMap: Record<number, boolean> = {};

  setRcaStatus(index: number, status: string): void {
    this.rcaStatusMap = { ...this.rcaStatusMap, [index]: status };
  }

  submitRcaStatus(index: number): void {
    this.rcaSubmittedMap = { ...this.rcaSubmittedMap, [index]: true };
  }

  /** Renders root cause text with highlighted section labels as safe HTML */
  formatRootCause(text: string): SafeHtml {
    if (!text) return this.sanitizer.bypassSecurityTrustHtml('');
    const labels = [
      'Primary Cause',
      'Secondary Cause',
      'Factors Affecting Recovery',
      'Prevention Actions',
      'Analysis Notes',
    ];
    const pattern = new RegExp(`(${labels.map(l => l.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')}):`, 'g');
    const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const html = escaped.replace(pattern, '<span style="font-weight:600;color:#0B2D71;">$1:</span>');
    return this.sanitizer.bypassSecurityTrustHtml(html.replace(/\n/g, '<br>'));
  }

  nptTotalCost() {
    if (this.apiNptSummary) return this.apiNptSummary.TotalNptCost || 0;
    if (!this.activeWell?.nptData || !Array.isArray(this.activeWell.nptData)) return 0;
    return this.activeWell.nptData.reduce((s: number, n: any) => s + n.cost, 0);
  }

  /** NPT hours for cost performance tab — prefer apiPerformance over apiNptSummary */
  get costPerfNptHours(): number {
    if (this.apiPerformance?.TotalNptHours != null) return this.apiPerformance.TotalNptHours;
    return this.OVERVIEW_NPT_TOTAL_HRS;
  }
  /** NPT cost for cost performance tab — prefer apiPerformance over apiNptSummary */
  get costPerfNptCost(): number {
    if (this.apiPerformance?.TotalNptCost != null) return this.apiPerformance.TotalNptCost;
    return this.nptTotalCost();
  }
  /** Total P&A duration (hours) for cost performance tab */
  get costPerfPnaDuration(): number {
    if (this.apiPerformance?.total_PnA_duration != null) return this.apiPerformance.total_PnA_duration;
    return this.totalPnaDuration || 1;
  }
  /** NPT % of P&A time for cost performance tab */
  get costPerfNptPct(): string {
    const dur = this.costPerfPnaDuration || 1;
    return (this.costPerfNptHours / dur * 100).toFixed(2);
  }
  maxFieldNptHours() { return this.maxDetailNptHours || 1; }
  maxNptHours() { return this.NPT_DATA.length ? Math.max(...this.NPT_DATA.map((d: any) => d.hours)) : 1; }
  maxNptCost() { return this.NPT_DATA.length ? Math.max(...this.NPT_DATA.map((d: any) => d.cost)) : 1; }
  maxTimelineDays() { return this.activeWell?.timeline?.length ? Math.max(...this.activeWell.timeline.map((d: any) => Math.max(d.planned, d.actual))) : 1; }
  maxPhaseCost() {
    const phases = this.COST_DATA.phaseWise || [];
    return phases.length ? Math.max(...phases.map((d: any) => Math.max(d.PlannedAFECost || 0, d.ActualPhaseFieldEstimate || 0))) : 1;
  }

  costBreakdownVariance(r: any) { return r.actual - r.afe; }
  costBreakdownVarPct(r: any) { return (r.actual - r.afe) / r.afe * 100; }
  maxCostValue() {
    const bd = this.COST_DATA.breakdown || [];
    return bd.length ? Math.max(...bd.map((d: any) => Math.max(d.afe || 0, d.actual || 0))) : 1;
  }

  statusBadgeClass(status: string) {
    const map: Record<string, string> = {
      complete: 'bg-green-50 text-green-700 border border-green-200',
      'in-review': 'bg-amber-50 text-amber-700 border border-amber-200',
      pending: 'bg-gray-100 text-gray-500',
    };
    return map[status] || map['pending'];
  }
  statusLabel(status: string) { return status === 'in-review' ? 'In Review' : status; }
  isNavLeafActive(itemId: string) {
    const nav = LEAF_NAV_MAP[itemId];
    return nav && nav.section === this.selectedSection && nav.subTab === this.selectedSubTab;
  }
  goToDashboardFromSidebar(leafId: string) {
    const nav = LEAF_NAV_MAP[leafId];
    if (!nav) return;
    this.mobileSidebarOpen = false;
    this.loading = true;
    this.activeNav = 'overview';
    this.activeHistoryId = null;
    this.selectedSection = nav.section;
    this.selectedSubTab = nav.subTab;
    // Close data quality popover when switching tabs
    this.showDataQualityPopover = false;
    // Lazily fetch the API needed for this tab
    this.loadApiForTab(nav.subTab);
    setTimeout(() => { this.loading = false; this.loaded = true; }, 400);
  }
  goToLanding() { this.screen = 'landing'; }
  /** Called by arrow button on landing page. Triggers MSAL login if not authenticated, then shows prompt page. */

  newChat() {
    // Clear current session on backend if one exists
    if (this.currentSessionId) {
      this.queryApi.clearSession(this.currentSessionId).subscribe({ error: () => {} });
    }
    this.currentSessionId = null;
    this.mobileSidebarOpen = false;
    this.activeNav = 'home';
    this.messages = [];
    this.activeHistoryId = null;
    this.router.navigate(['/prompt']);
  }

  goToMetrics() {
    this.mobileSidebarOpen = false;
    this.router.navigate(['/metrics']);
  }

  /**
   * Check if user has access to metrics dashboard and RCA table
   * Both use the same dashboardId from config.json
   */
  private async checkMetricsAccess(): Promise<void> {
    try {
      const config = this.appConfigService.getConfig();
      const dashboardId = config.dashboardId;
      
      if (!dashboardId || dashboardId.trim() === '') {
        this.hasMetricsAccess.set(false);
        this.hasRcaAccess.set(false);
        return;
      }
      
      const hasAccess = await this.graphService.isUserInRequiredGroup(dashboardId);
      this.hasMetricsAccess.set(hasAccess);
      this.hasRcaAccess.set(hasAccess);
    } catch (err) {
      this.hasMetricsAccess.set(false);
      this.hasRcaAccess.set(false);
    }
  }

  handlePrompt(promptInput: { text: string; type: string; wellId?: string; promptId?: string; selectedWells?: string[] }) {
    const promptLabel = promptInput.text;
    const promptType = promptInput.type || 'well';
    const wellId = promptInput.wellId;

    if (promptType === 'multi-well') {
      this.isMultiWellMode = true;
      this.multiWellSelectedNames = promptInput.selectedWells || [];
      this.messages = [{ role: 'user', content: promptLabel }];
      this.chatLoading = true;
      this.loading = true;
      this.activeNav = 'overview';
      this.selectedSection = 'overview';
      this.selectedSubTab = 'wells_summary';
      this.showDataQualityPopover = false;
      this.activeHistoryId = null;

      // Load field data for selected wells to clear old data
      if (promptInput.selectedWells && promptInput.selectedWells.length > 0) {
        this.loadFieldData(promptInput.selectedWells);
      }

      // Generate new session for this conversation
      const sessionId = QueryApiService.generateSessionId();
      this.currentSessionId = sessionId;

      this.queryApi.query({ question: promptLabel, session_id: sessionId }).subscribe({
        next: (res: any) => {
          this.currentSessionId = res.session_id;
          this.loading = false; this.chatLoading = false; this.loaded = true;
          this.promptSelectionView?.resetLoading();
          this.messages.push({ role: 'assistant', content: res.answer });
          // Reset pagination when new message arrives
          this.resetResultsPagination();
          // If wellId is present in response, load well data
          if (res.wellId) {
            this.loadWell(res.wellId);
          }
          this.pushChatHistoryEntry(this.messages, promptLabel, { section: 'overview', subTab: 'wells_summary', wellId: res.wellId });
        },
        error: () => {
          this.loading = false; this.chatLoading = false; this.loaded = true;
          this.promptSelectionView?.resetLoading();
          this.messages.push({ role: 'assistant', content: 'API unavailable. Please try again later.' });
          this.pushChatHistoryEntry(this.messages, promptLabel, { section: 'overview', subTab: 'wells_summary' });
        },
      });
      return;
    }

    this.isMultiWellMode = false;
    this.multiWellSelectedNames = [];

    // DO NOT try to match wellIds from WELL_DB.
    // wellIds are provided by the backend query API response only.
    // If promptType is 'well' and no wellId from backend, stay in prompt selection UI.
    if (promptType === 'well' && wellId) {
      this.loadWell(wellId);
    }

    this.messages = [{ role: 'user', content: promptLabel }];
    this.chatLoading = true;
    this.loading = true;

    // Generate new session for this conversation
    const newSessionId = QueryApiService.generateSessionId();
    this.currentSessionId = newSessionId;

    // Always call query API and navigate based on well_id in response
    this.loading = true;
    this.queryApi.query({ question: promptLabel, session_id: newSessionId }).subscribe({
      next: (res: any) => {
        this.currentSessionId = res.session_id;
        this.promptSelectionView?.resetLoading();
        this.messages.push({ role: 'assistant', content: res.answer });
        // Reset pagination when new message arrives
        this.resetResultsPagination();

        // Navigate based on well_id presence in response
        if (res.well_id) {
          // Well-level dashboard
          this.activeNav = 'overview';
          this.selectedSection = 'overview';
          this.selectedSubTab = 'wells_summary';
          this.showDataQualityPopover = false;
          this.activeHistoryId = null;
          this.chatLoading = false;  // Stop the chat loading indicator
          this.loadWell(res.well_id, true);
          this.pushChatHistoryEntry(this.messages, promptLabel, { section: 'overview', subTab: 'wells_summary', wellId: res.well_id });
        } else {
          // Field-level chatbot UI
          this.fieldPromptId = promptInput.promptId || '';
          this.activeNav = 'field';
          this.activeHistoryId = null;
          this.messages[this.messages.length - 1].fieldGraph = true;
          this.messages[this.messages.length - 1].fieldTable = this.fieldPromptId === 'field_npt_events' || this.fieldPromptId === 'field_npt_events_1yr';
          this.loading = false;
          this.chatLoading = false;
          this.loaded = true;
          this.pushChatHistoryEntry(this.messages, promptLabel, { section: 'field', subTab: 'dashboard' });
        }
      },
      error: () => {
        this.loading = false; this.chatLoading = false; this.loaded = true;
        this.promptSelectionView?.resetLoading();
        this.messages.push({ role: 'assistant', content: 'API unavailable. Please try again later.' });
        this.pushChatHistoryEntry(this.messages, promptLabel, { section: 'field', subTab: 'dashboard' });
      },
    });
  }

  getFieldPromptReply(promptId: string): string {
    const replies: Record<string, string> = {
      'field_npt_events': 'Here are the NPT events recorded in the Lost Hills field from November 2025 to March 2026 during Abandon Well – Permanent operations. Total NPT: 8.75 hrs | Total NPT Cost: $8,138.\n\nKey Insights:\n• All PERSONNEL delays are linked to regulatory witnessing (CalGEM).\n• Over $4,500 (≈55% of total NPT cost) is attributable to personnel wait time.\n• The EQPS event represents 34% of total NPT cost but only 23% of total duration.\n• Missing tools and last-minute rewiring indicate pre-job readiness gaps.',
      'field_npt_events_1yr': 'Here are the NPT events recorded in the Lost Hills field from March 2025 to March 2026 during Abandon Well – Permanent operations. Total NPT: 24.6 hrs | Total NPT Cost: $24,950.\n\nKey Insights:\n• CalGEM-related regulatory waits dominate NPT, accounting for 14.85 hrs (≈60%) of total duration and $14,600 (≈59%) of total cost. These delays span variance approvals and cement/hydrocarbon tag witnessing across multiple wells.\n• Transport and logistics delays contributed 4.0 hrs and $4,600, primarily due to waiting on cement trucks, cranes, and cement delivery sequencing—most notably on the Vulcan 9-6W well.\n• Equipment and execution-related issues account for 5.75 hrs and $5,750, including cementer wait time, cement pump mechanical failure, and DMW support required for heating and cutting bolts.\n• Vulcan 9-6W and Cahn 4-9WA are the highest NPT contributors, together representing 13.6 hrs (≈55%) of total NPT and $14,200 (≈57%) of total cost.\n• Multiple short-duration events accumulated into material NPT, indicating that while individual delays are modest, cumulative impacts across wells significantly affect field-level performance.\n• Pre-job coordination gaps are evident, particularly around regulator availability, equipment readiness, and third-party support alignment (transport, mechanics, DMW), suggesting opportunities for improved scheduling and confirmation ahead of execution.',
      'field_npt_causes': 'Across the Lost Hills P&A campaign, NPT falls into 4 root-cause categories:\n\n1. CalGEM Regulatory Holds (CMT + WO): 20.96 hrs, $27K — Cement tag witnesses, cleanout tag approvals, and variance approvals caused repeated standby. Pre-booking CalGEM inspectors is the highest-impact mitigation.\n\n2. Equipment Failures (EQP + EQPS): 11 hrs, $8.5K — BOP accumulator hose, CT reel weld, and sand-jet PTO failures. Pre-mob equipment inspection checklists required.\n\n3. Personnel/Procedural (WP + PR): 11.75 hrs, $15.5K — CT stuck pipe and dropped tubing incidents. Both are preventable with improved procedural controls.\n\n4. Rig Equipment (RREQ): 5.25 hrs, $7.5K — Rig brake and driveline failures. Daily mechanical inspection required.\n\nTop Lesson: CalGEM coordination is the single highest-leverage improvement opportunity — accounting for ~48% of total NPT hours.',
      'field_cost_impact': 'Cost impact of time overruns across the Lost Hills 5-well P&A campaign:\n\n• Total AFE: $1,038,300\n• Total Actual: $1,031,877\n• Net Campaign Variance: +$6,423 favorable overall\n\nHowever, 3 of 5 wells ran over AFE:\n• Vulcan 196R: +$123,970 over AFE (+92%) — largest single overrun\n• Sec 33 1-8WAR: +$13,836 over AFE (+10%)\n• Sec 29 1-1C: +$5,933 over AFE (+4%)\n\n2 wells came in under AFE:\n• Sec 16 6: -$127,666 under AFE (-40%)\n• Sec 33 2-7WA: -$22,496 under AFE (-7%)\n\nThe Sec 16 6 under-run largely offset the Vulcan 196R overrun. Total NPT cost: $51,000 across 43.21 hrs — representing approximately 4.9% of total actual spend.',
      'field_risk': 'Top NPT risk drivers ranked by total hours and cost impact across the Lost Hills P&A campaign:\n\n1. CMT (Cementing/CalGEM Witness): 10.25 hrs | $14.5K — 2 events on Sec 16 6 and Sec 29 1-1C\n2. WP (Waiting on Personnel/CT): 7 hrs | $7K — CT stuck pipe on Sec 33 2-7WA\n3. EQPS (Equipment/Service): 7 hrs | $6.5K — BOP hose + CT reel weld failures\n4. RREQ (Regulatory/Rig Equipment): 5.25 hrs | $7.5K — Rig brake + driveline failures\n5. WO (Waiting on Other/CalGEM): 4.96 hrs | $5K — 3 CalGEM hold events on Vulcan 196R\n6. PR (Dropped String): 4.75 hrs | $8.5K — 1 critical event on Sec 33 1-8WAR\n7. EQP (Equipment Failure): 4 hrs | $2K — CT reel weld on Sec 33 2-7WA\n\nHighest-cost single event: Sec 29 1-1C CalGEM witness ($9,000, 4.75 hrs)',
      'field_calgem': 'CalGEM regulatory holds affected 4 of 5 wells in the Lost Hills P&A campaign — the single most impactful NPT category:\n\n• Sec 29 1-1C: Unable to locate CalGEM rep for cleanout tag witness — 4.75 hrs, $9,000 (29-Dec-2023, year-end staffing risk)\n• Sec 16 6: CalGEM cement plug tag witness — 5.5 hrs, $5.5K (11-May-2023)\n• Vulcan 196R: Three separate holds (variance approval, cleanout tag, cement tag) — 4.96 hrs total, $5,000 (16-Feb-2024)\n• Sec 33 2-7WA: Indirect CalGEM coordination delays during CT cement operations\n\nTotal CalGEM-related NPT: ~15.21 hrs | ~$19.5K\n\nRoot Cause: CalGEM witness/approval requirements are not pre-coordinated before operations begin. Year-end and holiday periods compound risk due to reduced CDOG staffing.\n\nRecommendation: Establish a CalGEM Coordination Checklist — book inspector 72 hrs ahead, obtain all variance pre-approvals, and avoid scheduling witness-dependent jobs during year-end.',
    };
    return replies[promptId] || 'Field-level analysis for Lost Hills P&A campaign is displayed in the chart below. Total NPT: 43.21 hrs across 12 events ($51,000 total NPT cost). CalGEM coordination and equipment issues are the primary drivers.';
  }

  handleChatSend() {
    const text = this.chatDraft.trim();
    if (!text) return;
    this.messages.push({ role: 'user', content: text });
    this.shouldScroll = true;  // Trigger auto-scroll
    this.cdr.markForCheck();
    this.chatDraft = '';
    this.chatLoading = true;
    this.chatStatus = 'Processing…';

    // Mark that this is a follow-up question (not from prompt selection page)
    // This prevents navigation logic from triggering
    this.hasNavigatedForFirstQuestion = true;

    // Reuse the current session_id so the backend maintains conversation context
    const sessionId = this.currentSessionId || QueryApiService.generateSessionId();
    this.currentSessionId = sessionId;

    // Ensure WebSocket is connected for this session and always re-subscribe
    // Re-subscribing ensures the message handler uses fresh component state
    this.connectAndSubscribeWs(sessionId);
    const trySend = () => {
      if (this.wsService.isConnected) {
        // For multiwell mode, include well_ids only for the first question
        if (this.isMultiWellMode && !this.multiWellIdsSentForSession && this.fieldWellIds.length > 0) {
          this.wsService.sendQuestion(text, this.fieldWellIds);
          this.multiWellIdsSentForSession = true;
        } else {
          this.wsService.sendQuestion(text);
        }
      } else {
        setTimeout(trySend, 200);
      }
    };
    trySend();
  }

  /** Returns local mock reply for a follow-up question (used as fallback when backend is unavailable) */
  // Clicking a suggested follow-up question — send it directly


  pushChatHistoryEntry(msgs: any[], question: string, selOverride?: any) {
    const sel = selOverride || { section: this.selectedSection, subTab: this.selectedSubTab };
    if (this.activeHistoryId) {
      const existing = this.chatHistory.find(e => e.id === this.activeHistoryId);
      if (existing) {
        existing.messages = [...msgs];
        existing.timestamp = new Date().toLocaleString();
        return;
      }
    }
    // Prevent duplicate entries by checking if session already exists
    // Use cryptographically secure session ID generation
    const newId = this.currentSessionId || this.sessionService.getSessionId();
    this.activeHistoryId = newId;
    if (!this.chatHistory.some(e => e.id === newId)) {
      this.chatHistory.unshift({
        id: newId,
        session_id: newId,
        question: question.slice(0, 400),
        answer: '', section: sel.section, subTab: sel.subTab,
        fieldPromptId: sel.section === 'field' ? this.fieldPromptId : '',
        wellId: sel.section !== 'field' ? this._activeWellId : '',
        isMultiWell: this.isMultiWellMode,
        timestamp: new Date().toLocaleString(), messages: [...msgs],
      });
    }
  }

  loadChatHistoryEntry(entry: any) {
    this.mobileSidebarOpen = false;

    // session_id is the canonical identifier for API-loaded sessions
    const sessionId: string = entry.session_id || entry.id;
    if (!sessionId) {
      return;
    }
    
    this.activeHistoryId = sessionId;
    this.currentSessionId = sessionId;
    this.chatLoading = true;
    this.messages = [];
    this.cdr.detectChanges();

    // Helper to normalize role values from different API formats
    const normalizeRole = (role: string): 'user' | 'assistant' => {
      const r = (role || '').toLowerCase();
      if (r === 'user' || r === 'human') return 'user';
      return 'assistant'; // 'assistant', 'ai', 'bot', 'system', etc.
    };

    // Check if we're already on the same route to avoid double navigation
    const wellId = entry.wellId;
    let targetRoute = '';
    if (wellId) {
      targetRoute = `/well/${wellId}/${sessionId}`;
    } else if (entry.isMultiWell) {
      targetRoute = `/multiwell/${sessionId}`;
    } else {
      targetRoute = `/field/${sessionId}`;
    }
    
    const isAlreadyOnRoute = this.router.url === targetRoute;

    // Fetch full conversation from backend using conversationById API
    this.queryApi.getConversationById(sessionId).subscribe({
      next: (detail: any) => {
        const rawMessages = detail.messages || detail.chat_history || detail.conversation || [];
        const parsedMessages = rawMessages.map((m: any) => {
          const role = normalizeRole(m.role || m.type || 'assistant');
          const content = m.content || m.text || m.message || '';
          const msg: any = { role, content, message_id: m.message_id || '' };
          // Restore results and visualization for assistant messages
          if (role === 'assistant') {
            if (Array.isArray(m.results) && m.results.length > 0) {
              msg.results = m.results;
            }
            if (m.visualization) {
              msg.visualization = m.visualization;
              msg.chartOptions = this.buildChartOptions(m.results || [], m.visualization);
              msg.viewMode = 'table'; // default to table view
            }
            // Map feedback sentiment to feedbackType
            if (m.feedback && m.feedback.sentiment) {
              msg.feedbackType = m.feedback.sentiment === 'Like' ? 'like' : 'dislike';
            }
          }
          return msg;
        }).filter((m: any) => m.content); // drop empty messages
        
        // If messages are empty, show "No message found"
        if (parsedMessages.length === 0) {
          parsedMessages.push(
            { role: 'user', content: detail.description || entry.question || 'No question found' },
            { role: 'assistant', content: 'No message found or conversation incomplete. Please try asking a new question.', responseType: 'placeholder' }
          );
        }
        
        // Get well_id from the chat history entry (from getConversations API) instead of getConversationById
        const detailWellId = entry.wellId || detail.well_id;
        
        // Persist messages + queryResponse to sessionStorage BEFORE navigating
        // so the new component instance can pick them up in paramMap.subscribe
        sessionStorage.setItem(`query_response_${sessionId}`, JSON.stringify({
          queryResponse: this.lastQueryResponse,
          messages: parsedMessages,
        }));

        this.chatLoading = false;
        
        // If already on the same route, just load messages directly
        if (isAlreadyOnRoute) {
          this.messages = parsedMessages;
          this.lastQueryResponse = detail;
          this.shouldScroll = true;
          const chatRef = entry.isMultiWell ? this.fieldChatMessages : 
                          wellId ? this.wellChatMessages : this.fieldChatMessages;
          setTimeout(() => this.scrollChatToBottom(chatRef), 300);
          return;
        }
        
        // Navigate — component will be destroyed/recreated, messages restored from sessionStorage
        if (detailWellId) {
          this.router.navigate(['/well', detailWellId, sessionId]);
        } else if (entry.isMultiWell) {
          this.router.navigate(['/multiwell', sessionId]);
        } else {
          this.router.navigate(['/field', sessionId]);
        }
      },
      error: (err) => {
        // Fallback to locally cached entry properties
        let fallbackMessages = [...(entry.messages || [])];
        
        // If messages are empty, add "No message found" message
        if (fallbackMessages.length === 0) {
          fallbackMessages = [
            { role: 'user', content: entry.question || 'No question found' },
            { role: 'assistant', content: 'No message found or conversation incomplete. Please try asking a new question.' }
          ];
        }
        if (fallbackMessages.length === 0 && entry.description) {
          fallbackMessages.push({ role: 'user', content: entry.description });
        }

        // Persist to sessionStorage before navigating
        sessionStorage.setItem(`query_response_${sessionId}`, JSON.stringify({
          queryResponse: this.lastQueryResponse,
          messages: fallbackMessages,
        }));

        this.chatLoading = false;
        
        // If already on the same route, load messages directly
        if (isAlreadyOnRoute) {
          this.messages = fallbackMessages;
          this.shouldScroll = true;
          const chatRef = entry.isMultiWell ? this.fieldChatMessages : 
                          entry.wellId ? this.wellChatMessages : this.fieldChatMessages;
          setTimeout(() => this.scrollChatToBottom(chatRef), 300);
          return;
        }
        
        if (entry.wellId) {
          this.router.navigate(['/well', entry.wellId, sessionId]);
        } else if (entry.isMultiWell) {
          this.router.navigate(['/multiwell', sessionId]);
        } else {
          this.router.navigate(['/field', sessionId]);
        }
      },
    });
  }

  goBackFromField() { this.activeNav = 'home'; this.messages = []; this.activeHistoryId = null; }

  truncateQuestion(q: string) { return q.length > 36 ? q.slice(0, 33) + '' : q; }
  getFieldChatHistory() { return this.chatHistory; }

  priorityBadgeClass(p: string) {
    if (p === 'high') return 'bg-red-100 text-red-700';
    if (p === 'medium') return 'bg-amber-100 text-amber-700';
    return 'bg-gray-100 text-gray-500';
  }
  lessonBorderClass(p: string) {
    if (p === 'high') return 'border-red-200 bg-red-50/60';
    if (p === 'medium') return 'border-amber-200 bg-amber-50/60';
    return 'border-gray-200 bg-gray-50';
  }
  setSuggestedQuestion(q: string) { this.chatDraft = q; }
  abs(n: number) { return Math.abs(n); }
  isSection(s: string) { return this.selectedSection === s; }
  formatChatMsg(text: string) { return text.split('\n').filter((line: string) => line.trim() !== ''); }
  
  /**
   * Format markdown content with table support for chat messages
   * Converts markdown to HTML with proper table formatting
   */
  formatMarkdownToHtml(markdown: string): SafeHtml {
    if (!markdown) return this.sanitizer.bypassSecurityTrustHtml('');
    
    let html = markdown;

    // FIRST: Extract and replace separator lines BEFORE table conversion
    // This prevents them from being consumed by table regex
    // Pattern: |================xxxxxxx (equals signs at end of content)
    html = html.replace(/\|=+$/gm, '\n<hr style="border:none;height:2px;background:#d1d5db;margin:0rem 0;"/>\n');
    
    // Pattern: Standalone lines with equals (with or without pipe)
    html = html.replace(/^\|?=+$/gm, '<hr style="border:none;height:2px;background:#d1d5db;margin:0rem 0;"/>');

    // THEN: Convert markdown tables to HTML tables
    html = this.convertMarkdownTablesToHtml(html);

    // Convert headers
    html = html.replace(/^### (.+)$/gm, '<strong style="margin: 0rem 0; ">$1</strong>');
    html = html.replace(/^## (.+)$/gm, '<strong style="margin: 0rem 0 0rem 0; font-weight: 700;  padding-top: 0.75rem;">$1</strong>');
    html = html.replace(/^# (.+)$/gm, '<h1 style="margin: 0rem 0; font-weight: 700;">$1</h1>');

    // Convert bold: **text** -> <strong>text</strong>
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

    // Convert line breaks AFTER separator and table conversion
    html = html.replace(/\n\n+/g, '<br>');
    html = html.replace(/\n/g, '<br>');

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  /**
   * Convert all markdown tables in text to HTML tables
   */
  private convertMarkdownTablesToHtml(text: string): string {
    // Match markdown table: header row, separator row, and data rows
    // Handles tables with or without trailing newline on last row
    const tablePattern = /\|.+\n\|[\s\-:|]+\n(?:\|.+(?:\n|$))*/g;
    
    return text.replace(tablePattern, (match) => {
      // Verify it's actually a table before converting
      const lines = match.trim().split('\n');
      if (lines.length < 2) return match;
      
      // Check if second line is a separator (contains dashes and pipes)
      const isSeparator = /^[|\s:|\-]+$/.test(lines[1]);
      if (!isSeparator) return match;
      
      const result = this.convertMarkdownTableToHtml(match.trim());
      return result + '\n';
    });
  }

  /**
   * Convert a single markdown table to HTML table
   */
  private convertMarkdownTableToHtml(markdownTable: string): string {
    const lines = markdownTable.split('\n').filter(line => line.trim().length > 0);
    if (lines.length < 3) return markdownTable; // Need header, separator, and at least one data row

    const parseTableRow = (row: string): string[] => {
      return row.split('|').map(cell => cell.trim()).filter(cell => cell.length > 0);
    };

    const isSeparatorRow = (row: string): boolean => {
      const cleaned = row.replace(/[|\s:-]/g, '');
      return cleaned.length === 0;
    };

    // Find header and separator rows
    let headerRowIndex = 0;
    let separatorRowIndex = 1;

    for (let i = 1; i < lines.length; i++) {
      if (isSeparatorRow(lines[i])) {
        headerRowIndex = i - 1;
        separatorRowIndex = i;
        break;
      }
    }

    const headerCells = parseTableRow(lines[headerRowIndex]);
    const columnCount = headerCells.length;

    // Wrap table in scrollable container
    let html = '<div style="overflow-x:auto;max-width:100%;margin:0.75rem 0;border:1px solid #d1d5db;border-radius:4px;">';
    html += '<table style="width:100%;min-width:500px;border-collapse:collapse;background:#fff;font-size:12px;display:table;">';

    // Add header row
    html += '<thead style="background-color:#f3f4f6;"><tr>';
    headerCells.forEach(cell => {
      html += `<th style="padding:8px 12px;text-align:left;font-weight:600;color:#1f2937;border:1px solid #d1d5db;vertical-align:top;white-space:nowrap;">${cell}</th>`;
    });
    html += '</tr></thead>';

    // Add data rows
    html += '<tbody>';
    for (let i = separatorRowIndex + 1; i < lines.length; i++) {
      const cells = parseTableRow(lines[i]);
      if (cells.length > 0) {
        html += '<tr>';
        for (let j = 0; j < columnCount; j++) {
          const cellValue = cells[j] || '';
          html += `<td style="padding:8px 12px;color:#4b5563;border:1px solid #e5e7eb;vertical-align:top;line-height:1.4;">${cellValue}</td>`;
        }
        html += '</tr>';
      }
    }
    html += '</tbody></table></div>';

    return html;
  }
  
  /** Pagination helpers for results table */
  getPaginatedResults(results: Record<string, any>[]): Record<string, any>[] {
    if (!results || results.length === 0) return [];
    const start = this.resultsCurrentPage * this.resultsPageSize;
    const end = start + this.resultsPageSize;
    return results.slice(start, end);
  }
  
  getTotalPages(results: Record<string, any>[]): number {
    if (!results) return 0;
    return Math.ceil(results.length / this.resultsPageSize);
  }
  
  nextResultsPage(results: Record<string, any>[]): void {
    const totalPages = this.getTotalPages(results);
    if (this.resultsCurrentPage < totalPages - 1) {
      this.resultsCurrentPage++;
    }
  }
  
  prevResultsPage(): void {
    if (this.resultsCurrentPage > 0) {
      this.resultsCurrentPage--;
    }
  }
  
  resetResultsPagination(): void {
    this.resultsCurrentPage = 0;
  }
  
  /** Returns column header keys for a results table */
  resultColumns(results: Record<string, any>[]): string[] {
    if (!results || !Array.isArray(results) || results.length === 0) {
      return [];
    }
    const cols = Object.keys(results[0]);
    return cols;
  }

  /**
   * Smart table cell formatter:
   * - null/undefined → '—'
   * - whole integers (year, count, id) → no decimals, no thousands comma if 4-digit year
   * - other numbers → 2 decimal places with thousands comma
   * - strings → as-is
   */
  formatCellValue(value: any): string {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'number') {
      // Integer (no fractional part)
      if (Number.isInteger(value) || Math.abs(value - Math.round(value)) < 0.0001) {
        const intVal = Math.round(value);
        // Year-like (1900–2100): show without comma
        if (intVal >= 1900 && intVal <= 2100) return String(intVal);
        // Other integers: show with comma, no decimals
        return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(intVal);
      }
      // Float: 2 decimal places with comma
      return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
    }
    return String(value);
  }

  /** Toggle between 'table' and 'chart' view for a message */
  toggleViewMode(m: any, mode: 'table' | 'chart'): void {
    m.viewMode = mode;
    // Force change detection so Highcharts chart gets created/destroyed correctly
    this.cdr.detectChanges();
    // Give Highcharts time to initialise after the container becomes visible
    if (mode === 'chart') {
      setTimeout(() => {
        Highcharts.charts.forEach(c => c && c.reflow());
        this.cdr.detectChanges();
      }, 100);
    }
  }

  /** Detect if series need dual y-axes due to vastly different scales */
  private shouldUseDualAxes(aggregatedData: any[], viz: any): boolean {
    const datasets = viz.datasets || [];
    if (datasets.length < 2) return false;

    // Check if viz explicitly assigns datasets to different y-axis ids
    const axisIds = new Set(datasets.map((ds: any) => ds.y_axis_id || 'y1'));
    if (axisIds.size > 1) return true;

    // Calculate ranges for each dataset (include zero for null values)
    const ranges = datasets.map((ds: any) => {
      const columnName = ds.aggregation ? `${ds.column}_${ds.aggregation}` : ds.column;
      const values = aggregatedData
        .map((d: any) => {
          const val = d[columnName];
          // Convert string numbers to numeric values
          return typeof val === 'string' ? parseFloat(val) : val;
        })
        .filter((v: any) => v != null && !isNaN(v) && v !== 0); // Include nulls as 0, but filter out all zeros
      
      if (values.length === 0) return { min: 0, max: 1 }; // Default range if no data
      const absValues = values.map(Math.abs);
      const min = Math.min(...values);
      const max = Math.max(...values);
      return { min, max, absMax: Math.max(...absValues) };
    });

    // If scale ratio > 100x between any two datasets, use dual axes
    for (let i = 0; i < ranges.length; i++) {
      for (let j = i + 1; j < ranges.length; j++) {
        if (ranges[i].absMax > 0 && ranges[j].absMax > 0) {
          const ratio = ranges[i].absMax / ranges[j].absMax;
          if (ratio > 100 || ratio < 0.01) return true;
        }
      }
    }
    return false;
  }

  /** Build Highcharts options from results data and visualization config */
  buildChartOptions(results: any[], viz: any): Highcharts.Options {
    // Validate inputs
    if (!results || results.length === 0) {
      return { 
        chart: { type: 'column' }, 
        series: [], 
        title: { text: 'No data available' }
      };
    }
    
    if (!viz) {
      return { 
        chart: { type: 'column' }, 
        series: [], 
        title: { text: 'No visualization config' }
      };
    }
    
    
    // Check if this is a pie chart
    if (viz.viz_type === 'pie') {
      return this.buildPieChartOptions(results, viz);
    }

    // For all other chart types (column, line, area, etc.)
    // NOTE: Aggregation happens per-dataset after filtering (see below)
    // We need to extract unique x_column values from raw results for categories
    const categories = [...new Set(results.map(d => String(d[viz.x_column])))];
    
    // Check if we need dual y-axes for vastly different scales
    // Use raw results to determine dual axes need
    const useDualAxes = this.shouldUseDualAxes(results, viz);
    
    // Build series with proper yAxisIndex mapping
    const seriesList: Highcharts.SeriesOptionsType[] = (viz.datasets || []).map((ds: any, idx: number) => {
      let seriesType = ds.type === 'bar' ? 'column' : (ds.type || 'column');
      
      // Map any series type variations
      if (seriesType === 'column' || seriesType === 'bar') {
        seriesType = 'column';
      }
      
      // Always use y_axis_id from dataset spec to assign axis correctly
      // y1 → index 0 (left), y2 → index 1 (right)
      let yAxisIndex = 0;
      if (useDualAxes && (!viz.axes || Object.keys(viz.axes).length === 1)) {
        // Auto-distribute when no explicit multi-axis config
        yAxisIndex = idx > 0 ? 1 : 0;
      } else if (viz.axes) {
        const yAxisId = ds.y_axis_id || 'y1';
        yAxisIndex = Object.keys(viz.axes).indexOf(yAxisId);
        if (yAxisIndex < 0) yAxisIndex = 0;
      } else if (useDualAxes && idx > 0) {
        yAxisIndex = 1;
      }
      
      // FILTER results for this specific dataset if filter is specified
      let datasetResults = results;
      if (ds.filter && typeof ds.filter === 'object') {
        datasetResults = results.filter(row => {
          // Check if row matches all filter conditions
          return Object.entries(ds.filter).every(([key, value]) => row[key] === value);
        });
      }
      
      // Aggregate data for this dataset (if needed)
      const datasetAggregated = this.shouldAggregateData(datasetResults, viz)
        ? this.aggregateData(datasetResults, viz)
        : datasetResults;
      
      // Get the aggregated column name
      const columnName = ds.aggregation ? `${ds.column}_${ds.aggregation}` : ds.column;

      // Assign distinct colors: bars use dark/medium blues, lines use orange/teal
      const colorPalette = ['#0B2D71', '#2563EB', '#E8820C', '#00B2BD', '#60A5FA', '#93C5FD'];
      const seriesColor = colorPalette[idx] || colorPalette[idx % colorPalette.length];
      
      // CREATE LOOKUP MAP for fast access to aggregated data by x_column value
      const dataMap = new Map();
      datasetAggregated.forEach(r => {
        const key = String(r[viz.x_column]);
        dataMap.set(key, r);
      });
      
      // MAP ALL CATEGORIES to ensure every series has a value for each category
      // If category doesn't exist in this dataset, use null (will show as gap)
      const seriesData = categories.map(cat => {
        const row = dataMap.get(cat);
        if (row) {
          const yValue = row[columnName];
          const numericValue = typeof yValue === 'string' ? parseFloat(yValue) : yValue;
          return { y: isNaN(numericValue) ? null : numericValue, name: cat };
        } else {
          // Category doesn't exist in this dataset's filtered data
          return { y: null, name: cat };
        }
      });
      
      const seriesConfig: any = {
        type: seriesType,
        name: ds.label || ds.column,
        data: seriesData,
        yAxis: yAxisIndex,
        color: seriesColor,
        showInLegend: true,
      };
      
      // Apply type-specific styling
      if (seriesType === 'line') {
        seriesConfig.zIndex = 3; // Lines on top
        seriesConfig.lineWidth = 3;
        seriesConfig.marker = { radius: 5, enabled: true };
      } else if (seriesType === 'column') {
        seriesConfig.zIndex = 1;
        seriesConfig.borderRadius = 3;
      } else if (seriesType === 'area') {
        seriesConfig.lineWidth = 2;
        seriesConfig.marker = { radius: 4 };
      } else if (seriesType === 'spline') {
        seriesConfig.lineWidth = 2;
        seriesConfig.marker = { radius: 4 };
      }
      
      return seriesConfig as Highcharts.SeriesOptionsType;
    });

    // Build yAxis config
    let yAxisList: Highcharts.YAxisOptions[] = [];
    
    if (useDualAxes) {
      // Create two y-axes for different scales with synchronized zero origins
      const datasets = viz.datasets || [];
      
      // Calculate min/max for each dataset - group by y_axis_id or auto-split if useDualAxes
      const getValues = (idx: number) => {
        const ds = datasets[idx];
        // Apply the same filter as used in the series
        let datasetResults = results;
        if (ds.filter && typeof ds.filter === 'object') {
          datasetResults = results.filter(row => {
            return Object.entries(ds.filter).every(([key, value]) => row[key] === value);
          });
        }
        // Aggregate if needed
        const datasetAgg = this.shouldAggregateData(datasetResults, viz)
          ? this.aggregateData(datasetResults, viz)
          : datasetResults;
        
        const columnName = ds.aggregation 
          ? `${ds.column}_${ds.aggregation}` 
          : ds.column;
        return datasetAgg.map((d: any) => {
          const val = d[columnName] || 0;
          // Convert string numbers to numeric values
          return typeof val === 'string' ? parseFloat(val) : val;
        });
      };
      
      // Collect values for each y-axis
      const valuesY1: number[] = [];
      const valuesY2: number[] = [];
      
      datasets.forEach((ds: any, idx: number) => {
        const yAxisId = ds.y_axis_id || 'y1';
        const values = getValues(idx);
        
        // If viz doesn't explicitly define multiple axes but useDualAxes is true,
        // auto-distribute: first dataset → y1, second → y2, etc.
        if (!viz.axes || Object.keys(viz.axes).length === 1) {
          if (idx === 0) {
            valuesY1.push(...values);
          } else if (idx === 1) {
            valuesY2.push(...values);
          } else if (idx % 2 === 0) {
            valuesY1.push(...values);
          } else {
            valuesY2.push(...values);
          }
        } else {
          // Use explicit y_axis_id from viz
          if (yAxisId === 'y1') {
            valuesY1.push(...values);
          } else {
            valuesY2.push(...values);
          }
        }
      });
      
      const rawMin1 = valuesY1.length > 0 ? Math.min(...valuesY1) : 0;
      const rawMax1 = valuesY1.length > 0 ? Math.max(...valuesY1) : 1;
      const rawMin2 = valuesY2.length > 0 ? Math.min(...valuesY2) : 0;
      const rawMax2 = valuesY2.length > 0 ? Math.max(...valuesY2) : 1;
      
      // Ensure both sides have at least 10% of total range as headroom
      // This prevents near-zero max values from distorting the sync
      const ensureHeadroom = (min: number, max: number) => {
        const range = Math.max(Math.abs(min), Math.abs(max), 1);
        const headroom = range * 0.15;
        return {
          safeMin: min < -headroom * 0.1 ? min : -headroom,
          safeMax: max > headroom * 0.1 ? max : headroom
        };
      };
      
      const { safeMin: sMin1, safeMax: sMax1 } = ensureHeadroom(rawMin1, rawMax1);
      const { safeMin: sMin2, safeMax: sMax2 } = ensureHeadroom(rawMin2, rawMax2);
      
      // Compute the shared zero-fraction from bottom:
      // p = |min| / (|min| + max), use max across both axes so both fit
      const p1 = Math.abs(sMin1) / (Math.abs(sMin1) + sMax1);
      const p2 = Math.abs(sMin2) / (Math.abs(sMin2) + sMax2);
      const p = Math.max(p1, p2);
      const q = 1 - p; // fraction above zero
      
      // Scale each axis so zero sits at fraction p from bottom
      const scale1 = Math.max(Math.abs(sMin1) / p, sMax1 / q);
      const syncMin1 = -(p * scale1);
      const syncMax1 = q * scale1;
      
      const scale2 = Math.max(Math.abs(sMin2) / p, sMax2 / q);
      const syncMin2 = -(p * scale2);
      const syncMax2 = q * scale2;
      
      yAxisList = [
        {
          title: { 
            text: (viz.axes?.y1?.label) || datasets[0]?.label || 'Y-Axis 1',
            style: { fontSize: '13px', fontWeight: '600', color: '#0B2D71' },
            margin: 20,
          },
          opposite: false,
          labels: { format: '{value:,.2f}', style: { fontSize: '11px' }, x: -8 },
          gridLineWidth: 1,
          min: syncMin1,
          max: syncMax1,
          startOnTick: false,
          endOnTick: false,
          plotLines: [{ value: 0, color: '#cccccc', width: 1, zIndex: 2 }],
        },
        {
          title: { 
            text: (viz.axes?.y2?.label) || datasets[1]?.label || 'Y-Axis 2',
            style: { fontSize: '13px', fontWeight: '600', color: '#E8820C' },
            margin: 20,
          },
          opposite: true,
          labels: { format: '{value:,.2f}', style: { fontSize: '11px' }, x: 14 },
          gridLineWidth: 0,
          min: syncMin2,
          max: syncMax2,
          startOnTick: false,
          endOnTick: false,
        }
      ];
    } else if (viz.axes) {
      // Use viz.axes mapping if available
      for (const [key, axis] of Object.entries(viz.axes) as [string, any][]) {
        // Get all values for this axis by collecting from all datasets with this axis
        const allValues: number[] = [];
        
        (viz.datasets || []).forEach((ds: any, idx: number) => {
          const yAxisId = ds.y_axis_id || 'y1';
          if (yAxisId === key) {
            // Get the values for this dataset using the same filtering and aggregation logic
            let datasetResults = results;
            if (ds.filter && typeof ds.filter === 'object') {
              datasetResults = results.filter(row => {
                return Object.entries(ds.filter).every(([k, v]) => row[k] === v);
              });
            }
            const datasetAgg = this.shouldAggregateData(datasetResults, viz)
              ? this.aggregateData(datasetResults, viz)
              : datasetResults;
            
            const columnName = ds.aggregation 
              ? `${ds.column}_${ds.aggregation}` 
              : ds.column;
            const values = datasetAgg.map((d: any) => {
              const val = d[columnName] || 0;
              return typeof val === 'string' ? parseFloat(val) : val;
            });
            allValues.push(...values);
          }
        });
        
        const minVal = allValues.length > 0 ? Math.min(...allValues) : 0;
        const maxVal = allValues.length > 0 ? Math.max(...allValues) : 1;
        
        // Ensure range includes negative values
        const range = Math.max(Math.abs(minVal), Math.abs(maxVal), 1);
        const headroom = range * 0.1;
        
        yAxisList.push({
          title: { 
            text: axis.label || '', 
            style: { fontSize: '13px', fontWeight: '600', color: '#0B2D71' },
            margin: 10
          },
          opposite: axis.position === 'right',
          labels: { format: '{value:,.2f}', style: { fontSize: '11px' } },
          gridLineWidth: key === 'y1' ? 1 : 0,
          min: minVal < 0 ? minVal - headroom : -headroom,
          max: maxVal > 0 ? maxVal + headroom : headroom,
          plotLines: minVal < 0 ? [{ value: 0, color: '#cccccc', width: 1, zIndex: 2 }] : [],
        });
      }
    } else {
      // Default single y-axis - ensure it accommodates negative values
      const allValues: number[] = [];
      seriesList.forEach((s: any) => {
        if (s.data && Array.isArray(s.data)) {
          s.data.forEach((p: any) => {
            if (p && p.y != null) allValues.push(p.y);
          });
        }
      });
      
      const minVal = allValues.length > 0 ? Math.min(...allValues) : 0;
      const maxVal = allValues.length > 0 ? Math.max(...allValues) : 1;
      
      const range = Math.max(Math.abs(minVal), Math.abs(maxVal), 1);
      const headroom = range * 0.1;
      
      yAxisList = [{
        title: { text: '', style: { fontSize: '13px', fontWeight: '600', color: '#0B2D71' }, margin: 10 },
        labels: { format: '{value:,.2f}', style: { fontSize: '11px' } },
        gridLineWidth: 1,
        min: minVal < 0 ? minVal - headroom : -headroom,
        max: maxVal > 0 ? maxVal + headroom : headroom,
        plotLines: minVal < 0 ? [{ value: 0, color: '#cccccc', width: 1, zIndex: 2 }] : [],
      }];
    }

    return {
      chart: { type: 'column', height: 480, marginLeft: 130, marginBottom: 100, marginRight: useDualAxes ? 140 : 60, marginTop: 50, style: { fontFamily: 'inherit' } },
      title: { text: viz.title || '', style: { fontSize: '14px', fontWeight: '600' } },
      xAxis: {
        categories,
        title: { 
          text: viz.x_label || '', 
          // margin: 12,
          style: { fontSize: '12px', fontWeight: '600', color: '#0B2D71' } 
        },
        labels: { 
          rotation: categories.length > 5 ? -10 : 0, 
          align: categories.length > 5 ? 'right' : 'center',
          style: { fontSize: '11px' },
          y: 18
        },
        tickLength: 5,
        maxPadding: 0.01,
        tickPixelInterval: 50,
        offset: 0,
      },
      yAxis: yAxisList.length > 0 ? yAxisList : [{ title: { text: '' } }],
      series: seriesList,
      legend: { 
        enabled: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        floating: false,
        borderColor: '#e5e7eb',
        borderWidth: 1,
        borderRadius: 4,
        backgroundColor: '#ffffff',
        margin: 20,
        padding: 12,
        y: -5,
        x: 0,
        itemStyle: { 
          fontSize: '12px', 
          fontWeight: '600',
          color: '#1f2937'
        },
        itemHoverStyle: {
          color: '#0B2D71'
        },
        symbolRadius: 4
      },
      credits: { enabled: false },
      colors: ['#0B2D71', '#2563EB', '#60A5FA', '#93C5FD', '#BFDBFE'],
      plotOptions: {
        column: {
          borderRadius: 3,
          dataLabels: { enabled: false },
          grouping: true,
          pointPadding: 0.1,
          groupPadding: 0.15,
          showInLegend: true
        },
        line: {
          dataLabels: { enabled: false },
          showInLegend: true
        },
        area: {
          dataLabels: { enabled: false },
          showInLegend: true
        },
        spline: {
          dataLabels: { enabled: false },
          showInLegend: true
        },
        series: {
          showInLegend: true
        }
      },
      tooltip: {
        shared: true,
        headerFormat: '<span style="font-size:12px;font-weight:600">{point.key}</span><br/>',
        pointFormat: '<span style="color:{point.color}">●</span> {series.name}: <b>{point.y:,.2f}</b><br/>'
      }
    };
  }

  /** Determine if data needs aggregation or if it's already pre-aggregated */
  private shouldAggregateData(results: any[], viz: any): boolean {
    if (!results || results.length === 0) return false;

    // Check if any dataset has explicit aggregation
    const hasExplicitAggregation = (viz.datasets || []).some((ds: any) => ds.aggregation);
    if (hasExplicitAggregation) return true;

    // Check if there are duplicate x_column values (meaning we need to group/sum)
    const xKeys = results.map(r => String(r[viz.x_column]));
    const hasDuplicateKeys = xKeys.length !== new Set(xKeys).size;
    
    return hasDuplicateKeys;
  }

  /** Aggregate data by x_column if aggregation type is specified */
  private aggregateData(results: any[], viz: any): any[] {
    const grouped: { [key: string]: any } = {};
    const keyOrder: string[] = [];

    // Detect if there are multiple rows per x_column key (needs auto-sum)
    const xKeys = results.map(r => String(r[viz.x_column]));
    const hasDuplicateKeys = xKeys.length !== new Set(xKeys).size;

    // Group results by x_column
    results.forEach(row => {
      const key = String(row[viz.x_column]);
      if (!grouped[key]) {
        keyOrder.push(key);
        grouped[key] = { [viz.x_column]: row[viz.x_column] };
        // Initialize aggregation columns
        (viz.datasets || []).forEach((ds: any) => {
          if (ds.aggregation) {
            grouped[key][`${ds.column}_${ds.aggregation}`] = 0;
          } else {
            // If duplicates exist, initialize numeric columns to 0 for auto-sum
            grouped[key][ds.column] = hasDuplicateKeys && typeof row[ds.column] === 'number' ? 0 : row[ds.column];
          }
        });
      }

      // Perform aggregation
      (viz.datasets || []).forEach((ds: any) => {
        if (ds.aggregation === 'count') {
          grouped[key][`${ds.column}_count`]++;
        } else if (ds.aggregation === 'sum') {
          grouped[key][`${ds.column}_sum`] = (grouped[key][`${ds.column}_sum`] || 0) + (row[ds.column] || 0);
        } else if (hasDuplicateKeys && typeof row[ds.column] === 'number') {
          // Auto-sum numeric columns when multiple rows share the same x key
          grouped[key][ds.column] = (grouped[key][ds.column] || 0) + (row[ds.column] || 0);
        }
      });
    });

    // Convert back to array preserving insertion order
    return keyOrder.map(key => grouped[key]);
  }

  /** Build pie chart options */
  buildPieChartOptions(results: any[], viz: any): Highcharts.Options {
    const dataset = viz.datasets?.[0];
    if (!dataset) {
      return { chart: { type: 'pie' }, series: [{ type: 'pie', data: [] }] };
    }

    // Build pie chart data: name from x_column, value from data column
    const pieData = results.map(r => {
      const yValue = r[dataset.column];
      // Convert string numbers to numeric values for proper chart rendering
      const numericValue = typeof yValue === 'string' ? parseFloat(yValue) : yValue;
      return {
        name: r[viz.x_column],
        y: isNaN(numericValue) ? 0 : numericValue
      };
    });

    return {
      chart: { type: 'pie', height: 420, style: { fontFamily: 'inherit' } },
      title: { text: viz.title || '', style: { fontSize: '14px', fontWeight: '600' } },
      subtitle: { text: viz.reasoning || '', style: { fontSize: '11px', color: '#666', marginBottom: '20px' } },
      tooltip: {
        headerFormat: '',
        pointFormat: '<span style="color:{point.color}">●</span> <b>{point.name}</b>: {point.y:,.0f} ({point.percentage:.1f}%)',
        useHTML: true
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            enabled: true,
            format: '<b>{point.name}</b>: {point.percentage:.1f}%',
            style: { fontSize: '12px', color: '#333', fontWeight: 'bold' }
          },
          showInLegend: true
        }
      },
      legend: {
        enabled: true,
        layout: 'vertical',
        align: 'right',
        verticalAlign: 'middle',
        itemStyle: { fontSize: '12px' }
      },
      series: [
        {
          type: 'pie',
          name: dataset.label || 'Total',
          data: pieData
        } as any
      ],
      credits: { enabled: false },
      colors: ['#0B2D71', '#2563EB', '#60A5FA', '#93C5FD', '#BFDBFE', '#DBEAFE', '#E0E7FF']
    };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Data Quality Methods
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Check if data quality information is available
   */
  hasDataQuality(): boolean {
    if (this.selectedSubTab === 'dashboard') {
      // NPT Summary tab - check apiNptSummary
      if (this.isMultiWellMode && this.apiNptSummary && Array.isArray(this.apiNptSummary)) {
        return this.apiNptSummary.some((well: any) => well.data_quality);
      } else if (!this.isMultiWellMode && this.apiNptSummary && !Array.isArray(this.apiNptSummary)) {
        return !!this.apiNptSummary.data_quality;
      }
    } else if (this.selectedSubTab === 'cost_performance') {
      // Cost & Performance tab - check apiPerformance
      if (this.isMultiWellMode && this.apiPerformance && Array.isArray(this.apiPerformance)) {
        return this.apiPerformance.some((well: any) => well.data_quality);
      } else if (!this.isMultiWellMode && this.apiPerformance && !Array.isArray(this.apiPerformance)) {
        return !!this.apiPerformance.data_quality;
      }
    }
    return false;
  }

  /**
   * Get the data quality object from the appropriate API data source
   */
  private getDataQualityObject(): any {
    if (this.selectedSubTab === 'dashboard') {
      // NPT Summary tab
      if (this.isMultiWellMode && this.apiNptSummary && Array.isArray(this.apiNptSummary)) {
        // For multi-well, aggregate quality from first well or return most critical
        const wellWithQuality = this.apiNptSummary.find((well: any) => well.data_quality);
        return wellWithQuality?.data_quality || null;
      } else if (!this.isMultiWellMode && this.apiNptSummary && !Array.isArray(this.apiNptSummary)) {
        return this.apiNptSummary.data_quality || null;
      }
    } else if (this.selectedSubTab === 'cost_performance') {
      // Cost & Performance tab
      if (this.isMultiWellMode && this.apiPerformance && Array.isArray(this.apiPerformance)) {
        const wellWithQuality = this.apiPerformance.find((well: any) => well.data_quality);
        return wellWithQuality?.data_quality || null;
      } else if (!this.isMultiWellMode && this.apiPerformance && !Array.isArray(this.apiPerformance)) {
        return this.apiPerformance.data_quality || null;
      }
    }
    return null;
  }

  /**
   * Get all wells with their data quality information for multi-well mode
   */
  getMultiWellDataQuality(): Array<{wellName: string; score: number; band: string; warnings: any[]}> {
    const wells: Array<{wellName: string; score: number; band: string; warnings: any[]}> = [];
    
    let apiData: any[] = [];
    if (this.selectedSubTab === 'dashboard' && this.apiNptSummary && Array.isArray(this.apiNptSummary)) {
      apiData = this.apiNptSummary;
    } else if (this.selectedSubTab === 'cost_performance' && this.apiPerformance && Array.isArray(this.apiPerformance)) {
      apiData = this.apiPerformance;
    }

    for (const wellData of apiData) {
      if (!wellData.data_quality) continue;

      const dataQuality = wellData.data_quality;
      const scores: number[] = [];
      const sections: Array<{ name: string; score: number; warnings: any }> = [];

      // Collect scores from all sections
      if (dataQuality.well_summary?.data_quality_summary?.quality_score !== undefined) {
        const score = dataQuality.well_summary.data_quality_summary.quality_score;
        scores.push(score);
        sections.push({
          name: 'well_summary',
          score: score,
          warnings: dataQuality.well_summary.data_quality_warnings
        });
      }
      if (dataQuality.npt_events?.data_quality_summary?.quality_score !== undefined) {
        const score = dataQuality.npt_events.data_quality_summary.quality_score;
        scores.push(score);
        sections.push({
          name: 'npt_events',
          score: score,
          warnings: dataQuality.npt_events.data_quality_warnings
        });
      }
      if (dataQuality.phases?.data_quality_summary?.quality_score !== undefined) {
        const score = dataQuality.phases.data_quality_summary.quality_score;
        scores.push(score);
        sections.push({
          name: 'phases',
          score: score,
          warnings: dataQuality.phases.data_quality_warnings
        });
      }

      if (scores.length === 0) continue;

      const minScore = Math.min(...scores);
      const minSection = sections.reduce((min, section) => 
        section.score < min.score ? section : min
      );

      const band = minScore >= 85 ? 'reliable' : minScore >= 60 ? 'caution' : 'high_caution';
      const warnings = this.ensureArray(minSection.warnings);

      wells.push({
        wellName: wellData.WellName || 'Unknown Well',
        score: minScore,
        band: band,
        warnings: warnings
      });
    }

    return wells;
  }

  /**
   * Extract minimum quality score from all data_quality_summary sections
   * For multi-well mode, returns the minimum score across all wells
   */
  getDataQualityScore(): number {
    if (this.isMultiWellMode) {
      const wells = this.getMultiWellDataQuality();
      if (wells.length === 0) return 0;
      return Math.min(...wells.map(w => w.score));
    }

    const dataQuality = this.getDataQualityObject();
    if (!dataQuality) return 0;

    const scores: number[] = [];

    // Collect scores from all sections
    if (dataQuality.well_summary?.data_quality_summary?.quality_score !== undefined) {
      scores.push(dataQuality.well_summary.data_quality_summary.quality_score);
    }
    if (dataQuality.npt_events?.data_quality_summary?.quality_score !== undefined) {
      scores.push(dataQuality.npt_events.data_quality_summary.quality_score);
    }
    if (dataQuality.phases?.data_quality_summary?.quality_score !== undefined) {
      scores.push(dataQuality.phases.data_quality_summary.quality_score);
    }

    // Return minimum score, or 0 if no scores found
    return scores.length > 0 ? Math.min(...scores) : 0;
  }

  /**
   * Determine quality band based on score
   */
  getDataQualityBand(): 'reliable' | 'caution' | 'high_caution' {
    const score = this.getDataQualityScore();
    if (score >= 85) return 'reliable';
    if (score >= 60) return 'caution';
    return 'high_caution';
  }

  /**
   * Get CSS class for quality button based on band
   */
  getDataQualityButtonClass(): string {
    const band = this.getDataQualityBand();
    switch (band) {
      case 'reliable': return 'quality-btn-green';
      case 'caution': return 'quality-btn-amber';
      case 'high_caution': return 'quality-btn-red';
      default: return 'quality-btn-green';
    }
  }

  /**
   * Get warnings only from the section with the minimum quality score
   */
  getDataQualityWarnings(): any[] {
    const dataQuality = this.getDataQualityObject();
    if (!dataQuality) return [];

    // Find which section has the minimum score
    const sections: Array<{ name: string; score: number; warnings: any }> = [];

    if (dataQuality.well_summary?.data_quality_summary?.quality_score !== undefined) {
      sections.push({
        name: 'well_summary',
        score: dataQuality.well_summary.data_quality_summary.quality_score,
        warnings: dataQuality.well_summary.data_quality_warnings
      });
    }
    if (dataQuality.npt_events?.data_quality_summary?.quality_score !== undefined) {
      sections.push({
        name: 'npt_events',
        score: dataQuality.npt_events.data_quality_summary.quality_score,
        warnings: dataQuality.npt_events.data_quality_warnings
      });
    }
    if (dataQuality.phases?.data_quality_summary?.quality_score !== undefined) {
      sections.push({
        name: 'phases',
        score: dataQuality.phases.data_quality_summary.quality_score,
        warnings: dataQuality.phases.data_quality_warnings
      });
    }

    if (sections.length === 0) return [];

    // Find the section with minimum score
    const minSection = sections.reduce((min, section) => 
      section.score < min.score ? section : min
    );

    // Return only warnings from the minimum score section
    return this.ensureArray(minSection.warnings);
  }

  /**
   * Helper to ensure value is an array
   */
  private ensureArray(value: any): any[] {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') return [value];
    if (value) return [value];
    return [];
  }

  /**
   * Toggle data quality popover visibility
   */
  toggleDataQualityPopover(): void {
    this.showDataQualityPopover = !this.showDataQualityPopover;
  }

  /**
   * Close data quality popover
   */
  closeDataQualityPopover(): void {
    this.showDataQualityPopover = false;
  }

  /**
   * Get count of wells without NPT events from the NPT Summary API response
   * Includes wells that were requested but didn't return any data (no NPT events)
   */
  getWellsWithoutNptCount(): number {
    // Check the NPT Summary API response directly
    if (!this.apiNptSummary || !Array.isArray(this.apiNptSummary)) {
      return 0;
    }

    // Count wells from the API that have no NPT events or zero NPT hours
    const wellsWithoutNptFromApi = this.apiNptSummary.filter((well: any) => {
      const hasNoEvents = !well.NptEvents || well.NptEvents.length === 0;
      const hasZeroHours = well.TotalNptHours === 0 || well.TotalNptHours === undefined;
      return hasNoEvents || hasZeroHours;
    }).length;

    // Also count wells that were selected but didn't return any data from the API
    // These are wells with no NPT events at all
    const selectedWellIds = new Set(this.fieldWellIds || []);
    const returnedWellIds = new Set(this.apiNptSummary.map((well: any) => well.WellID));
    const missingWellsCount = selectedWellIds.size - returnedWellIds.size;

    return wellsWithoutNptFromApi + missingWellsCount;
  }

  /**
   * Check if there are wells without NPT in multi-well mode
   */
  hasWellsWithoutNpt(): boolean {
    return this.getWellsWithoutNptCount() > 0;
  }

  /**
   * Download comprehensive PDF report for current well(s) with all data
   */
  downloadTabDataAsPdf() {
    this.downloadingPdf = true;
    this.cdr.detectChanges();

    // Use setTimeout to allow UI to update first
    setTimeout(() => {
      this.performPdfDownload();
    }, 100);
  }

  /**
   * Perform the actual PDF download operation
   */
  private performPdfDownload() {
    try {
      
      // Include ALL sections in the API payload
      const includeSections = [
        'well_summary',
        'npt_summary',
        'causal_summary',
        'performance_summary',
        'lessons_learned'
      ];

      // Get well IDs
      const wellIds = this.isMultiWellMode 
        ? this.fieldWellIds 
        : (this.activeWell?.id ? [this.activeWell.id] : []);

      if (wellIds.length === 0) {
        this.downloadingPdf = false;
        this.cdr.detectChanges();
        alert('No wells selected for PDF export');
        return;
      }

      
      // Call the lookback-aggregate API
      this.wellDataApi.getLookbackAggregate(wellIds, includeSections).subscribe({
        next: (response: any) => {
          
          if (!response || response.status !== 'success') {
            this.downloadingPdf = false;
            this.cdr.detectChanges();
            alert('Failed to fetch lookback data');
            return;
          }

          try {
            this.generatePdfFromLookbackData(response);
          } catch (error) {
            alert('Failed to generate PDF. Error: ' + String(error));
          } finally {
            this.downloadingPdf = false;
            this.cdr.detectChanges();
          }
        },
        error: (error: any) => {
          this.downloadingPdf = false;
          this.cdr.detectChanges();
          alert('Failed to download PDF data. Please try again.');
        }
      });

    } catch (error) {
      this.downloadingPdf = false;
      this.cdr.detectChanges();
      alert('An unexpected error occurred');
    }
  }

  /**
   * Generate PDF document from lookback aggregate response - matches UI display exactly
   */
  private generatePdfFromLookbackData(data: any) {
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const isMultiWell = data.well_ids && data.well_ids.length > 1;
      
      let yPosition = 20;
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 12;
      const contentWidth = pageWidth - (2 * margin);

      // Helper: Check page break
      const checkPageBreak = (neededSpace: number = 15) => {
        if (yPosition + neededSpace > pageHeight - 15) {
          doc.addPage();
          yPosition = 15;
          return true;
        }
        return false;
      };

      // Helper: Add section header (blue bar)
      const addSectionHeader = (title: string) => {
        checkPageBreak(20);
        doc.setFillColor(11, 45, 113);
        doc.rect(margin, yPosition, contentWidth, 8, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(11);
        doc.text(title, margin + 3, yPosition + 5.5);
        yPosition += 12;
        doc.setTextColor(0, 0, 0);
      };

      // Helper: Add sub header
      const addSubHeader = (title: string) => {
        checkPageBreak(12);
        doc.setFontSize(10);
        doc.setTextColor(11, 45, 113);
        doc.text(title, margin, yPosition);
        yPosition += 6;
        doc.setTextColor(0, 0, 0);
      };

      // Helper: Add key-value row
      const addKeyValue = (label: string, value: any, indent: number = 0) => {
        checkPageBreak(6);
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.text(label + ':', margin + indent, yPosition);
        doc.setTextColor(0, 0, 0);
        doc.text(String(value || '—'), margin + indent + 45, yPosition);
        yPosition += 5;
      };

      // Helper: Add full-width table with auto column sizing and NO TEXT TRUNCATION
      const addFullTable = (headers: string[], rows: any[][], colWidths?: number[]) => {
        if (rows.length === 0) return;
        
        const numCols = headers.length;
        const defaultColWidth = contentWidth / numCols;
        const widths = colWidths || headers.map(() => defaultColWidth);
        
        // Header row
        checkPageBreak(12);
        doc.setFillColor(11, 45, 113);
        doc.rect(margin, yPosition, contentWidth, 7, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        
        let xPos = margin;
        headers.forEach((header, i) => {
          doc.text(header, xPos + 1, yPosition + 4.5);
          xPos += widths[i];
        });
        yPosition += 8;

        // Data rows with text wrapping and proper padding
        doc.setFontSize(7);
        rows.forEach((row, rowIndex) => {
          // Split each cell text into lines
          const wrappedCells = row.map((cell, colIndex) => {
            const cellText = String(cell ?? '—');
            return doc.splitTextToSize(cellText, widths[colIndex] - 2);
          });
          
          // Get max height needed for this row
          const rowHeight = Math.max(...wrappedCells.map(lines => lines.length)) * 4 + 3;
          checkPageBreak(rowHeight + 2);
          
          // Alternating row colors
          if (rowIndex % 2 === 0) {
            doc.setFillColor(248, 249, 250);
            doc.rect(margin, yPosition, contentWidth, rowHeight, 'F');
          }
          
          doc.setTextColor(0, 0, 0);
          xPos = margin;
          let maxY = yPosition;
          
          wrappedCells.forEach((lines, colIndex) => {
            let cellY = yPosition + 2.5; // Padding from top of cell
            lines.forEach((line: string) => {
              doc.text(line, xPos + 1, cellY);
              cellY += 4;
              maxY = Math.max(maxY, cellY);
            });
            xPos += widths[colIndex];
          });
          
          yPosition = maxY + 2;
        });
        yPosition += 3;
      };

      // Helper: Format currency
      const fmtCurrency = (val: number) => {
        if (val === undefined || val === null) return '—';
        if (Math.abs(val) >= 1000) {
          return '$' + (val / 1000).toFixed(1) + 'K';
        }
        return '$' + val.toFixed(0);
      };

      // Helper: Format number with decimals
      const fmt2 = (val: number) => {
        if (val === undefined || val === null) return '—';
        return val.toFixed(2);
      };

      // Helper: Format date to match UI (MM-DD-YYYY HH:MM)
      const fmtDate = (dateStr: string) => {
        if (!dateStr) return '—';
        try {
          const d = new Date(dateStr);
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          const year = d.getFullYear();
          const hours = String(d.getHours()).padStart(2, '0');
          const minutes = String(d.getMinutes()).padStart(2, '0');
          return `${month}-${day}-${year} ${hours}:${minutes}`;
        } catch {
          return dateStr;
        }
      };

      // ==================== TITLE PAGE ====================
      doc.setFontSize(22);
      doc.setTextColor(11, 45, 113);
      doc.text('P&A Insights Report', margin, yPosition);
      yPosition += 10;
      
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Generated: ${new Date().toLocaleString()}`, margin, yPosition);
      yPosition += 5;
      doc.text(`Wells: ${data.well_ids?.length || 1}`, margin, yPosition);
      yPosition += 15;

      // ==================== 1. WELL SUMMARY ====================
      if (data.well_summary?.data?.length > 0) {
        addSectionHeader('WELL SUMMARY');

        if (isMultiWell) {
          // Multi-well: Table format like UI
          const headers = ['Well Name', 'Field', 'Area', 'Job Type', 'Start Date', 'End Date'];
          const colWidths = [35, 30, 35, 35, 25, 25];
          const rows = data.well_summary.data.map((w: any) => [
            w.WellName || '—',
            w.FieldName || '—',
            w.Area || '—',
            w.JobType || '—',
            fmtDate(w.StartDate),
            fmtDate(w.EndDate)
          ]);
          addFullTable(headers, rows, colWidths);
        } else {
          // Single well: Card format like UI
          const well = data.well_summary.data[0];
          
          // Well Identification Card
          addSubHeader('Well Identification');
          addKeyValue('Well Name', well.WellName);
          addKeyValue('Field', well.FieldName);
          addKeyValue('Area', well.Area);
          addKeyValue('Operator', well.CurrentOperator);
          addKeyValue('County/State', `${well.CountyParish || '—'} / ${well.StateProvince || '—'}`);
          addKeyValue('Governing Authority', well.GoverningAuthority || '—');
          yPosition += 5;

          // Well Background Card
          addSubHeader('Well Background');
          addKeyValue('Well Class', well.WellClass);
          addKeyValue('Well Type', well.WellType);
          addKeyValue('Total Depth', well.TotalDepth ? `${well.TotalDepth} ft` : '—');
          
          // Check if InitialSpudDate is valid (not null/empty)
          const hasInitialSpudDate = well.InitialSpudDate && 
                                     well.InitialSpudDate !== 'null' && 
                                     well.InitialSpudDate.toString().trim() !== '';
          
          addKeyValue('Initial Spud Date', hasInitialSpudDate ? fmtDate(well.InitialSpudDate) : '—');
          addKeyValue('Age of Well', hasInitialSpudDate && well.AgeOfWell ? this.formatAge(well.AgeOfWell) : '—');
          addKeyValue('Well Safe', well.Well_Safe);
          addKeyValue('Plugging Material', well.Plugging_Material);
          addKeyValue('Equipment Used', well.EquipmentUsed);
          yPosition += 5;

          // Job Identification Card
          addSubHeader('Job Identification');
          addKeyValue('Job Type', well.JobType);
          addKeyValue('Complexity', well.Complexity);
          addKeyValue('Technical Result', well.TechnicalResult);
          addKeyValue('Start Date', fmtDate(well.StartDate));
          addKeyValue('End Date', fmtDate(well.EndDate));
          yPosition += 5;
        }
      }

      // ==================== 2. NPT SUMMARY ====================
      if (data.npt_summary?.data?.length > 0) {
        checkPageBreak(40);
        addSectionHeader('NPT SUMMARY');

        // Calculate totals for KPI cards
        let totalPnaDuration = 0;
        let totalNptHours = 0;
        let totalNptCost = 0;
        let totalEvents = 0;
        const allEvents: any[] = [];

        data.npt_summary.data.forEach((well: any) => {
          totalPnaDuration += well.total_PnA_duration || 0;
          totalNptHours += well.TotalNptHours || 0;
          totalNptCost += well.TotalNptCost || 0;
          if (well.NptEvents) {
            totalEvents += well.NptEvents.length;
            well.NptEvents.forEach((ev: any) => {
              allEvents.push({ ...ev, WellName: well.WellName });
            });
          }
        });

        // KPI Summary
        addSubHeader('Summary');
        addKeyValue('Total P&A Duration', `${fmt2(totalPnaDuration)} hrs`);
        addKeyValue('Non Productive Time', `${fmt2(totalNptHours)} hrs`);
        addKeyValue('NPT Cost', fmtCurrency(totalNptCost));
        addKeyValue('Total NPT Events', `${totalEvents} event(s)`);
        yPosition += 8;

        // NPT Events Table - Full data like UI
        if (allEvents.length > 0) {
          addSubHeader('NPT Events');
          
          if (isMultiWell) {
            const headers = ['Well', 'Category/Detail', 'Description', 'Duration (hrs)', 'Cost', 'Service Contractor', 'Start Date', 'End Date'];
            const colWidths = [20, 25, 45, 20, 16, 25, 20, 20];
            const rows = allEvents.map((ev: any) => [
              ev.WellName || '—',
              `${ev.MajorCategory || '—'}/${ev.Detail || '—'}`,
              ev.Description || '—',
              fmt2(ev.NetProblemDuration),
              fmtCurrency(ev.ProblemCost),
              ev.ServiceContractors || '—',
              fmtDate(ev.StartDate),
              fmtDate(ev.EndDate)
            ]);
            addFullTable(headers, rows, colWidths);
          } else {
            const headers = ['Category/Detail', 'Description', 'Duration (hrs)', 'Cost', 'Service Contractor', 'Start Date', 'End Date'];
            const colWidths = [28, 55, 22, 20, 25, 22, 22];
            const rows = allEvents.map((ev: any) => [
              `${ev.MajorCategory || '—'}/${ev.Detail || '—'}`,
              ev.Description || '—',
              fmt2(ev.NetProblemDuration),
              fmtCurrency(ev.ProblemCost),
              ev.ServiceContractors || '—',
              fmtDate(ev.StartDate),
              fmtDate(ev.EndDate)
            ]);
            addFullTable(headers, rows, colWidths);
          }
        }
      }

      // ==================== 3. CAUSAL ANALYSIS ====================
      if (data.causal_summary?.data?.length > 0) {
        checkPageBreak(30);
        addSectionHeader('CAUSAL ANALYSIS (AI Generated)');

        data.causal_summary.data.forEach((well: any) => {
          if (well.causes && well.causes.length > 0) {
            if (isMultiWell) {
              addSubHeader(well.WellName || 'Well');
            }
            
            // NPT Event, Root Cause, Status - NO Confidence column (matching UI)
            const headers = ['NPT Event', 'Root Cause', 'Status'];
            const colWidths = [50, 95, 30];
            const rows = well.causes.map((c: any) => [
              c.Description || '—',
              c.RootCause || '—',
              c.VerificationStatus || '—'
            ]);
            addFullTable(headers, rows, colWidths);
          }
        });
      }

      // ==================== 4. COST & PERFORMANCE ====================
      if (data.performance_summary?.data?.length > 0) {
        checkPageBreak(40);
        addSectionHeader('COST & PERFORMANCE SUMMARY');


        // Calculate totals for KPI cards
        let totalPlannedAFE = 0;
        let totalActualCost = 0;
        const allPhases: any[] = [];

        data.performance_summary.data.forEach((well: any) => {
          totalPlannedAFE += well.AFEAmount || 0;
          totalActualCost += well.TotalFieldCostEstimate || 0;
          if (well.Phases) {
            well.Phases.forEach((p: any) => {
              allPhases.push({ ...p, WellName: well.WellName });
            });
          }
        });

        

        const totalVariance = totalPlannedAFE - totalActualCost;

        // KPI Summary
        addSubHeader('Cost Summary');
        addKeyValue('Planned AFE Cost', fmtCurrency(totalPlannedAFE));
        addKeyValue('Actual Field Cost Estimate', fmtCurrency(totalActualCost));
        addKeyValue('Variance', `${totalVariance >= 0 ? '+' : ''}${fmtCurrency(totalVariance)}`);
        yPosition += 8;

        // Phase-wise Duration Table
        if (allPhases.length > 0) {
          addSubHeader('Phase-wise Duration');
          
          if (isMultiWell) {
            const headers = ['Well Name', 'Well Phase', 'Sub Phase', 'Planned (hrs)', 'Actual (hrs)', 'Deviation (hrs)'];
            const colWidths = [28, 20, 20, 28, 28, 28];
            const rows = allPhases.map((p: any) => [
              p.WellName || '—',
              p.WellPhase || '—',
              p.SubWellPhase || '—',
              fmt2(p.PlannedAFEDuration),
              fmt2(p.ActualDuration),
              `${p.DurationDeviation >= 0 ? '+' : ''}${fmt2(p.DurationDeviation)}`
            ]);
            addFullTable(headers, rows, colWidths);
          } else {
            const headers = ['Phase', 'Sub Phase', 'Start Date', 'End Date', 'Planned (hrs)', 'Actual (hrs)', 'Deviation'];
            const colWidths = [22, 25, 26, 26, 26, 26, 26];
            const rows = allPhases.map((p: any) => [
              p.WellPhase || '—',
              p.SubWellPhase || '—',
              fmtDate(p.ActualStartDate),
              fmtDate(p.ActualEndDate),
              fmt2(p.PlannedAFEDuration),
              fmt2(p.ActualDuration),
              `${p.DurationDeviation >= 0 ? '+' : ''}${fmt2(p.DurationDeviation)}`
            ]);
            addFullTable(headers, rows, colWidths);
          }

          yPosition += 5;

          // Phase-wise Cost Table
          addSubHeader('Phase-wise Cost');
          
          if (isMultiWell) {
            const headers = ['Well Name', 'Well Phase', 'Sub Phase', 'Planned AFE ($)', 'Actual Cost ($)', 'Variance ($)'];
            const colWidths = [28, 20, 20, 28, 28, 28];
            const rows = allPhases.map((p: any) => [
              p.WellName || '—',
              p.WellPhase || '—',
              p.SubWellPhase || '—',
              fmtCurrency(p.PlannedAFECost),
              fmtCurrency(p.ActualPhaseFieldEstimate),
              `${p.CostVariance >= 0 ? '+' : ''}${fmtCurrency(p.CostVariance)}`
            ]);
            addFullTable(headers, rows, colWidths);
          } else {
            const headers = ['Phase', 'Sub Phase', 'Planned AFE ($)', 'Actual Cost ($)', 'Variance ($)'];
            const colWidths = [30, 30, 40, 40, 40];
            const rows = allPhases.map((p: any) => [
              p.WellPhase || '—',
              p.SubWellPhase || '—',
              fmtCurrency(p.PlannedAFECost),
              fmtCurrency(p.ActualPhaseFieldEstimate),
              `${p.CostVariance >= 0 ? '+' : ''}${fmtCurrency(p.CostVariance)}`
            ]);
            addFullTable(headers, rows, colWidths);
          }
        }
      }

      // ==================== 5. LESSONS LEARNED ====================
      if (data.lessons_learned?.data?.length > 0) {
        checkPageBreak(30);
        addSectionHeader('LESSONS LEARNED (AI Generated)');

        data.lessons_learned.data.forEach((well: any, wellIndex: number) => {
          if (wellIndex > 0) checkPageBreak(15);
          
          if (isMultiWell) {
            doc.setFontSize(10);
            doc.setTextColor(11, 45, 113);
            doc.setFont('helvetica', 'bold');
            doc.text(`Lookback Well: ${well.well_name || 'Well'}`, margin, yPosition);
            yPosition += 8;
            doc.setFont('helvetica', 'normal');
          }

          if (well.report) {
            doc.setFontSize(8);
            doc.setTextColor(0, 0, 0);
            
            const reportText = well.report;
            
            // Process the entire report line by line
            const lines = reportText.split('\n');
            let inTable = false;
            let tableTitle = '';
            let tableLines: string[] = [];
            
            for (let i = 0; i < lines.length; i++) {
              const line = lines[i];
              const trimmed = line.trim();
              
              // Skip empty lines at start of table or consecutive empty lines
              if (!trimmed && !inTable) {
                yPosition += 2;
                continue;
              }
              
              // Check for markdown header (## Title)
              const headerMatch = trimmed.match(/^##\s+(.+)$/);
              if (headerMatch) {
                // If we're in a table, render it first before starting new section
                if (inTable && tableLines.length > 0) {
                  checkPageBreak(20);
                  yPosition += 3;
                  
                  // Only print table title if it's not empty
                  if (tableTitle) {
                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(8.5);
                    doc.setTextColor(11, 45, 113);
                    doc.text(tableTitle, margin, yPosition);
                    yPosition += 6;
                  }
                  doc.setFont('helvetica', 'normal');
                  
                  // Parse and render table - filter out separator rows (---|----|----) and empty rows
                  const filteredTableLines = tableLines.filter((l: string) => {
                    // Skip separator lines that only contain pipes, dashes, colons, and spaces
                    if (l.match(/^\|[\s\-:|]*\|$/)) {
                      return false; // This is a separator, filter it out
                    }
                    return true;
                  });
                  
                  if (filteredTableLines.length > 1) {
                    const rows: string[][] = [];
                    let headers: string[] = [];
                    
                    filteredTableLines.forEach((tableLine: string, idx: number) => {
                      const cells = tableLine.split('|')
                        .map((c: string) => c.trim())
                        .filter((c: string) => c.length > 0);
                      
                      if (idx === 0 && cells.length > 0) {
                        headers = cells;
                      } else if (cells.length > 0) {
                        rows.push(cells);
                      }
                    });
                    
                    if (headers.length > 0 && rows.length > 0) {
                      const colWidth = (contentWidth - 4) / headers.length;
                      const colWidths = headers.map(() => colWidth);
                      addFullTable(headers, rows, colWidths);
                    }
                  }
                  
                  inTable = false;
                  tableLines = [];
                  tableTitle = '';
                }
                
                // Check if this is a table section (Similar Historical Wells or References)
                const title = headerMatch[1];
                if (title.toLowerCase().includes('similar') || title.toLowerCase().includes('reference')) {
                  // Start table collection
                  inTable = true;
                  tableTitle = title;
                  tableLines = [];
                } else {
                  // Regular header
                  checkPageBreak(6);
                  doc.setFont('helvetica', 'bold');
                  doc.setFontSize(8.5);
                  doc.setTextColor(11, 45, 113);
                  doc.text(title, margin, yPosition);
                  yPosition += 5;
                  doc.setFont('helvetica', 'normal');
                  doc.setFontSize(8);
                  doc.setTextColor(0, 0, 0);
                }
                continue;
              }
              
              // If in table mode, collect table lines
              if (inTable) {
                if (trimmed.includes('|')) {
                  tableLines.push(trimmed);
                } else if (!trimmed) {
                  // Skip empty lines while in table mode - don't end table yet
                  continue;
                } else if (tableLines.length > 0) {
                  // End of table - render it (non-empty, non-pipe line ends table)
                  checkPageBreak(20);
                  yPosition += 3;
                  
                  // Only print table title if it's not empty
                  if (tableTitle) {
                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(8.5);
                    doc.setTextColor(11, 45, 113);
                    doc.text(tableTitle, margin, yPosition);
                    yPosition += 6;
                  }
                  doc.setFont('helvetica', 'normal');
                  
                  // Parse table - filter out separator rows (---|----|----) and empty rows
                  const filteredTableLines = tableLines.filter((l: string) => {
                    // Skip separator lines that only contain pipes, dashes, colons, and spaces
                    if (l.match(/^\|[\s\-:|]*\|$/)) {
                      return false; // This is a separator, filter it out
                    }
                    return true;
                  });
                  
                  if (filteredTableLines.length > 1) {
                    const rows: string[][] = [];
                    let headers: string[] = [];
                    
                    filteredTableLines.forEach((tableLine: string, idx: number) => {
                      const cells = tableLine.split('|')
                        .map((c: string) => c.trim())
                        .filter((c: string) => c.length > 0);
                      
                      if (idx === 0 && cells.length > 0) {
                        headers = cells;
                      } else if (cells.length > 0) {
                        rows.push(cells);
                      }
                    });
                    
                    if (headers.length > 0 && rows.length > 0) {
                      const colWidth = (contentWidth - 4) / headers.length;
                      const colWidths = headers.map(() => colWidth);
                      addFullTable(headers, rows, colWidths);
                    }
                  }
                  
                  inTable = false;
                  tableLines = [];
                  tableTitle = '';
                  
                  // Process current line as regular text
                  if (trimmed) {
                    i--; // Reprocess this line
                  }
                }
                continue;
              }
              
              // Regular text processing
              if (!trimmed) continue;
              
              // Check for pipe characters - could be table data for References section
              if (trimmed.startsWith('|') && !inTable) {
                // Start table mode for inline references table
                inTable = true;
                tableTitle = '';
                tableLines = [];
                tableLines.push(trimmed);
                continue;
              }
              
              // Check for "References:" label (with or without ** or other formatting)
              if (trimmed.toLowerCase().match(/^[\*\s]*references[\*\s]*:\s*$/i)) {
                checkPageBreak(6);
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(8.5);
                doc.setTextColor(11, 45, 113);
                doc.text('References:', margin, yPosition);
                yPosition += 5;
                doc.setFont('helvetica', 'normal');
                // Start table mode for references
                inTable = true;
                tableTitle = '';  // No title needed, already printed
                tableLines = [];
                continue;
              }
              
              // Check for structured labels (Category:, What happened:, etc.)
              // Match labels like "Category: ..." or "**What happened:** ..."
              const labelMatch = trimmed.match(/^\*?\*?(Category|What happened|Watch for|Root cause|Prevent by|If it happens|Note)\*?\*?:\s*(.*)$/i);
              if (labelMatch) {
                checkPageBreak(6);
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(8);
                doc.setTextColor(11, 45, 113);
                doc.text(`${labelMatch[1]}:`, margin, yPosition);
                yPosition += 4;
                
                if (labelMatch[2]) {
                  doc.setFont('helvetica', 'normal');
                  doc.setTextColor(0, 0, 0);
                  // Remove markdown bold markers from content
                  const cleanContent = labelMatch[2].replace(/\*\*([^\*]+)\*\*/g, '$1');
                  const contentLines = doc.splitTextToSize(cleanContent, contentWidth - 4);
                  contentLines.forEach((contentLine: string) => {
                    checkPageBreak(4);
                    doc.text(contentLine, margin + 2, yPosition);
                    yPosition += 4;
                  });
                }
                continue;
              }
              
              // Check for bullet points
              if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
                checkPageBreak(4);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(0, 0, 0);
                const bulletText = trimmed.substring(2).replace(/\*\*([^\*]+)\*\*/g, '$1');
                const bulletLines = doc.splitTextToSize('• ' + bulletText, contentWidth - 6);
                bulletLines.forEach((bulletLine: string) => {
                  checkPageBreak(4);
                  doc.text(bulletLine, margin + 2, yPosition);
                  yPosition += 4;
                });
                continue;
              }
              
              // Regular text - remove all ** markers and render as plain text
              checkPageBreak(4);
              doc.setFont('helvetica', 'normal');
              doc.setTextColor(0, 0, 0);
              // Remove all markdown bold markers
              const cleanText = trimmed.replace(/\*\*([^\*]+)\*\*/g, '$1');
              const textLines = doc.splitTextToSize(cleanText, contentWidth - 4);
              textLines.forEach((textLine: string) => {
                checkPageBreak(4);
                doc.text(textLine, margin + 2, yPosition);
                yPosition += 4;
              });
            
            // End of for loop
            }
            
            // If we ended while still in table mode, render the table
            if (inTable && tableLines.length > 0) {
              checkPageBreak(20);
              yPosition += 3;
              
              // Only print table title if it's not empty
              if (tableTitle) {
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(8.5);
                doc.setTextColor(11, 45, 113);
                doc.text(tableTitle, margin, yPosition);
                yPosition += 6;
              }
              doc.setFont('helvetica', 'normal');
              
              const filteredTableLines = tableLines.filter((l: string) => {
                // Skip separator lines that only contain pipes, dashes, colons, and spaces
                if (l.match(/^\|[\s\-:|]*\|$/)) {
                  return false; // This is a separator, filter it out
                }
                return true;
              });
              
              if (filteredTableLines.length > 1) {
                const rows: string[][] = [];
                let headers: string[] = [];
                
                filteredTableLines.forEach((tableLine: string, idx: number) => {
                  const cells = tableLine.split('|')
                    .map((c: string) => c.trim())
                    .filter((c: string) => c.length > 0);
                  
                  if (idx === 0 && cells.length > 0) {
                    headers = cells;
                  } else if (cells.length > 0) {
                    rows.push(cells);
                  }
                });
                
                if (headers.length > 0 && rows.length > 0) {
                  const colWidth = (contentWidth - 4) / headers.length;
                  const colWidths = headers.map(() => colWidth);
                  addFullTable(headers, rows, colWidths);
                }
              }
            }
            
            yPosition += 6;
          }
        });
      }

      // ==================== SAVE PDF ====================
      const timestamp = new Date().toISOString().split('T')[0];
      const wellName = isMultiWell ? 'Multi-Well' : (data.well_summary?.data?.[0]?.WellName || 'Well');
      const fileName = `${wellName.replace(/[^a-zA-Z0-9]/g, '_')}_Report_${timestamp}.pdf`;
      doc.save(fileName);


    } catch (error) {
      throw error;
    }
  }
}
